(() => {
  'use strict';

  const SECTION_IDS = Object.freeze(['cart-drawer', 'cart-icon-bubble']);
  const REQUEST_TIMEOUT = 12000;
  const PRODUCT_CACHE_TTL = 60000;
  const PROTECTION_CACHE_TTL = 10000;
  const CART_LOCK = 'myart-cart-mutations';
  const CART_REVIEW_KEY = 'myart-cart-review-v1';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function parseDocument(html) {
    return new DOMParser().parseFromString(String(html || ''), 'text/html');
  }

  function safeUrl(value, fallback = '#') {
    if (typeof value !== 'string' || !value.trim()) return fallback;
    try {
      const parsed = new URL(String(value || ''), window.location.origin);
      return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : fallback;
    } catch {
      return fallback;
    }
  }

  function setImageSource(image, value) {
    const url = safeUrl(typeof value === 'object' ? value?.src : value, '');
    image.hidden = !url;
    if (url) image.src = url;
    else image.removeAttribute('src');
  }

  function safePhotoUrl(value) {
    if (typeof value !== 'string' || value.length > 4096 || /[\s\\]/.test(value)) return '';
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' || url.host !== 'cdn.shopify.com' || url.username || url.password ||
          !url.pathname.startsWith('/s/files/') || !url.pathname.includes('/uploads/') ||
          !/\.(?:jpe?g|png|webp|avif|gif)$/i.test(url.pathname)) return '';
      return url.href;
    } catch { return ''; }
  }

  function normalizeHandle(value) {
    if (typeof value === 'string') return value.trim();
    if (value && typeof value === 'object') {
      if (typeof value.handle === 'string') return value.handle.trim();
      if (value.handle && typeof value.handle.handle === 'string') return value.handle.handle.trim();
    }
    return '';
  }

  class MyArtCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.productCache = new Map();
      this.productReadQueue = [];
      this.activeProductReads = 0;
      this.productRetryAt = 0;
      this.recommendationCache = new Map();
      this.mutationChain = Promise.resolve();
      this.pendingMutations = 0;
      this.renderVersion = 0;
      this.renderRequestVersion = 0;
      this.sheetSequence = 0;
      this.activeElement = null;
      this.currentProductState = null;
      this.connected = false;
      this.requests = new Set();
      this.lineIdentities = new Map();
      this.lineSnapshots = new Map();
      this.lastCart = null;
      this.cartReadVersion = 0;
      this.cartReadWriteRevision = -1;
      this.needsReview = false;
      this.busyRefreshes = 0;
      this.productRequestVersion = 0;
      this.checkoutPending = false;
      this.protectionIntent = null;
      this.writeRevision = 0;
      this.confirmedWriteRevision = 0;
      this.lastInputWasKeyboard = false;
      this.pendingNote = null;
      this.cartAnimations = new Set();
      this.controlFeedback = new Set();
      this.feedbackNodes = new Set();
      this.confettiAnimations = new Set();
      this.pendingPromotionCelebration = 0;
      this.externalAdditions = new WeakMap();
      this.pendingQuantity = null;
      this.pendingRemoval = null;
      this.removalIntents = new Map();
      this.bundledSections = null;
      this.cartRetryAt = 0;
      this.dismissRevision = 0;
      this.consentDialogs = new Set();
      this.consentResumePending = false;
    }

    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.eventController = new AbortController();
      const { signal } = this.eventController;
      this.motionPreference = window.matchMedia?.('(prefers-reduced-motion: reduce)');
      this.motionPreference?.addEventListener?.('change', () => {
        if (this.motionPreference.matches) { this.cancelCartMotion(); this.cancelPromotionCelebration(); this.drawerMotion?.cancel(); if (this.closing && this.dialog?.open) this.dialog.close(); }
      }, { signal });

      document.addEventListener('click', (event) => this.onDocumentClick(event), { signal });
      document.addEventListener('pointerdown', () => { this.lastInputWasKeyboard = false; }, { signal });
      document.addEventListener('keydown', () => { this.lastInputWasKeyboard = true; }, { signal });
      document.addEventListener('submit', (event) => {
        if (this.onProductFormSubmit(event)) return;
        const form = event.target;
        const cartPath = new URL(this.dataset.cartUrl || '/cart', window.location.origin).pathname.replace(/\/$/, '');
        if (form instanceof HTMLFormElement && form.id === 'cart' &&
            window.location.pathname.replace(/\/$/, '') === cartPath && event.submitter?.name === 'checkout') {
          event.preventDefault();
          this.checkout(event.submitter);
        }
      }, { signal });
      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.addEventListener('change', (event) => this.onChange(event), { signal });
      this.addEventListener('submit', (event) => this.onSubmit(event), { signal });
      this.addEventListener('pointerdown', (event) => this.onRailPointerDown(event), { signal });
      document.addEventListener('pointermove', (event) => this.onRailPointerMove(event), { signal });
      document.addEventListener('pointerup', (event) => this.endRailDrag(event), { signal });
      document.addEventListener('pointercancel', () => this.cancelRailDrag(), { signal });
      this.addEventListener('click', (event) => {
        const suppressed = this.railDragClick;
        this.railDragClick = null;
        if (suppressed && suppressed.contains(event.target)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { signal, capture: true });
      this.addEventListener('keydown', (event) => this.onRailKeyDown(event), { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.requestReconciliation();
        else if (this.closing && this.dialog?.open) { this.drawerMotion?.cancel(); this.dialog.close(); }
      }, { signal });
      window.addEventListener('pageshow', (event) => {
        // History can restore cached HTML without restoring the JS heap (Firefox).
        // That document carries the old server-rendered cart just like a bfcache page.
        const historyNavigation = window.performance?.getEntriesByType?.('navigation')?.[0]?.type === 'back_forward';
        if (event.persisted || historyNavigation) this.requestReconciliation({ historyRestore: true });
      }, { signal });
      window.addEventListener('online', () => this.requestReconciliation(), { signal });
      const receiveReview = () => {
        this.needsReview = true;
        this.showError('The last change could not be confirmed. Review the cart and select Check cart before continuing.');
        this.setBusy(this.pendingMutations > 0);
      };
      window.addEventListener('storage', (event) => {
        if (event.key === CART_REVIEW_KEY && event.newValue === '1') receiveReview();
      }, { signal });
      window.addEventListener('resize', () => this.updateLayoutWidth(), { signal });
      window.addEventListener('orientationchange', () => this.updateLayoutWidth(), { signal });
      if (typeof BroadcastChannel === 'function') {
        this.channel = new BroadcastChannel(CART_LOCK);
        this.channel.addEventListener('message', (event) => {
          if (event.data?.reviewRequired) receiveReview();
          this.requestReconciliation();
        }, { signal });
      }
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source !== 'myart-cart-drawer' && !(event?.source === 'product-form' && event?.cartController === this)) this.requestReconciliation();
        });
      }

      this.cacheDom();
      this.connectConsentPresentation(signal);
      this.recordProductVisit();
      this.configureCartTrigger();
      this.scheduleEnhancements();

      const reconcileLegacy = () => {
        if (document.querySelector('[data-myart-legacy-accessory]')) this.requestReconciliation();
      };
      document.addEventListener('shopify:section:load', reconcileLegacy, { signal });
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', reconcileLegacy, { once: true, signal });
      } else reconcileLegacy();

      if (this.dataset.openOnLoad === 'true') {
        queueMicrotask(() => { if (this.connected) this.open(undefined, { refresh: true }); });
      }
    }

    disconnectedCallback() {
      this.connected = false;
      this.consentResumePending = false;
      this.consentDialogs.clear();
      this.drawerMotion?.cancel();
      this.closeSheet({ restoreFocus: false });
      this.pendingQuantity = null;
      this.bundledSections = null;
      // Keep issued weak tokens so a detached, in-flight upload can report uncertainty.
      this.clearPhotoPreview();
      this.pendingPromotionCelebration = 0;
      this.cancelRailDrag();
      this.cancelCartMotion();
      this.cancelPromotionCelebration();
      this.controlFeedback.clear();
      this.renderControlFeedback();
      this.eventController?.abort();
      this.dialogController?.abort();
      this.refreshController?.abort();
      this.requests.forEach((controller) => controller.abort());
      this.requests.clear();
      this.productRequestVersion += 1;
      this.unsubscribeCart?.();
      this.channel?.close();
      this.pendingNote?.flush();
      if (this.dialog?.open) this.dialog.close();
      document.body.classList.remove('myart-cart-open');
      document.body.style.removeProperty('--myart-cart-page-padding');
    }

    updateCashbackPoints() {
      const output = this.querySelector('[data-cart-cashback-points]');
      if (!output) return;
      const totalText = output.dataset.cashbackTotal;
      const total = /^\d+$/.test(totalText || '') ? Number(totalText) : NaN;
      const currency = output.dataset.cashbackCurrency;
      const baseCurrency = output.dataset.cashbackBaseCurrency;
      const context = window.Shopify?.currency;
      // This store's loyalty unit is one USD cent, not a local money subunit.
      // rate converts shop USD into the page's active presentment currency.
      const rateValue = context?.rate;
      const rate = typeof rateValue === 'number' || (typeof rateValue === 'string' && rateValue.trim())
        ? Number(rateValue) : NaN;
      let points = null;
      if (baseCurrency === 'USD' && Number.isSafeInteger(total) && total >= 0) {
        if (currency === 'USD') points = Math.round(total / 20);
        else if (/^[A-Z]{3}$/.test(currency || '') && context?.active === currency &&
            Number.isFinite(rate) && rate > 0) points = Math.round(total / rate / 20);
      }
      const valid = Number.isSafeInteger(points) && points >= 0;
      output.textContent = valid ? String(points) : '—';
      output.setAttribute('aria-label', valid
        ? `${points} cashback points; 100 points equal 1 US dollar`
        : 'Cashback points unavailable');
    }

    cacheDom() {
      this.updateCashbackPoints();
      this.dialogController?.abort();
      this.dialogController = new AbortController();
      const { signal } = this.dialogController;

      this.dialog = this.querySelector('[data-cart-dialog]');
      this.view = this.querySelector('[data-cart-view]');
      if (!this.dialog) return;

      this.dialog.addEventListener('wheel', (event) => this.onRailWheel(event), { signal, passive: false });
      this.dialog.addEventListener('close', () => this.onDialogClose(), { signal });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.getOpenSheet()) {
          event.preventDefault();
          this.closeSheet();
        } else this.close();
      }, { signal });
      this.dialog.addEventListener('keydown', (event) => {
        const sheet = this.getOpenSheet();
        if (event.key === 'Tab' && sheet) {
          const controls = [...sheet.querySelectorAll(FOCUSABLE_SELECTOR)].filter((node) => node.getClientRects().length && !node.closest('[hidden], [inert]'));
          const first = controls[0] || sheet;
          const last = controls[controls.length - 1] || sheet;
          if ((event.shiftKey && (document.activeElement === first || !sheet.contains(document.activeElement))) ||
              (!event.shiftKey && (document.activeElement === last || !sheet.contains(document.activeElement)))) {
            event.preventDefault();
            (event.shiftKey ? last : first).focus({ preventScroll: true });
          }
          return;
        }
        if (event.key !== 'Escape') return;
        this.lastInputWasKeyboard = true;
        event.preventDefault();
        event.stopPropagation();
        if (this.getOpenSheet()) this.closeSheet();
        else this.close();
      }, { signal });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      }, { signal });
    }

    configureCartTrigger() {
      const trigger = document.getElementById('cart-icon-bubble');
      if (!trigger) return;
      if (trigger instanceof HTMLAnchorElement && !trigger.hasAttribute('href')) {
        trigger.href = this.dataset.cartUrl || '/cart';
      }
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-controls', 'MyArtCartDrawer');
      trigger.setAttribute('aria-expanded', String(Boolean(this.dialog?.open)));
    }

    onDocumentClick(event) {
      const wishlistAdd = event.target instanceof Element ? event.target.closest('[data-wishlist-cart-add]') : null;
      if (wishlistAdd && !event.defaultPrevented && event.button <= 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        if (wishlistAdd.disabled || wishlistAdd.hasAttribute('data-cart-pending')) return;
        this.withControlFeedback(wishlistAdd, () => this.addFromWishlist(wishlistAdd));
        return;
      }
      const quantity = event.target instanceof Element ? event.target.closest('[data-myart-legacy-accessory] .together-qty-btn') : null;
      if (quantity && !event.defaultPrevented && event.button <= 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        const card = quantity.closest('[data-myart-legacy-accessory]');
        this.withControlFeedback(quantity, () => this.changeLegacyAccessory(card, quantity.classList.contains('plus') ? 1 : -1, quantity));
        return;
      }
      const trigger = event.target instanceof Element ? event.target.closest('#cart-icon-bubble') : null;
      if (!trigger || trigger.closest('cart-drawer')) return;
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
      if (this.open(trigger, { refresh: true })) event.preventDefault();
    }

    connectConsentPresentation(signal) {
      // Consentmo owns consent. We only coordinate its visible dialogs with
      // our native modal, which otherwise makes an outside banner inert.
      const visible = (element) => Boolean(element && element.getClientRects().length &&
        getComputedStyle(element).visibility !== 'hidden' && getComputedStyle(element).display !== 'none');
      const root = document.querySelector('csm-cookie-consent')?.shadowRoot || document;
      if (visible(root.querySelector('[data-csm-button-type="accept"]'))) this.consentDialogs.add('notice');
      if (visible(root.querySelector('.cc-settings-dialog'))) this.consentDialogs.add('preferences');
      document.addEventListener('consentmo:dialogOpened', (event) => {
        if (!['notice', 'preferences'].includes(event.detail?.dialog)) return;
        this.consentDialogs.add(event.detail.dialog);
        if (!this.dialog?.open || this.closing) return;
        this.consentResumePending = true;
        this.consentPause = true;
        this.drawerMotion?.cancel();
        this.drawerMotion = null;
        this.dialog.close();
      }, { signal });
      document.addEventListener('consentmo:dialogClosed', (event) => {
        if (!['notice', 'preferences'].includes(event.detail?.dialog)) return;
        this.consentDialogs.delete(event.detail.dialog);
        queueMicrotask(() => {
          if (!this.connected || this.consentDialogs.size || !this.consentResumePending) return;
          this.consentResumePending = false;
          this.open(this.activeElement, { refresh: true });
        });
      }, { signal });
    }

    open(triggeredBy, { refresh = false } = {}) {
      this.updateCashbackPoints();
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return false;
      if (this.consentDialogs.size) {
        if (triggeredBy instanceof HTMLElement) this.setActiveElement(triggeredBy);
        this.consentResumePending = true;
        return true;
      }
      this.consentPause = false;
      const wasOpen = this.dialog.open;
      const wasClosing = this.closing;
      const currentTransform = wasClosing ? getComputedStyle(this.dialog).transform : null;
      if (wasOpen && !wasClosing) {
        if (refresh) this.requestReconciliation();
        else this.scheduleEnhancements();
        return true;
      }
      this.drawerMotion?.cancel();
      this.drawerMotion = null;
      this.closing = false;
      if (triggeredBy instanceof HTMLElement) this.setActiveElement(triggeredBy);
      this.dialog.removeAttribute('data-cart-closing');
      if (!this.dialog.open) {
        try {
          this.preferCloseFocus = this.lastInputWasKeyboard;
          this.updateLayoutWidth();
          const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
          const pagePadding = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
          document.body.style.setProperty('--myart-cart-page-padding', `${pagePadding + scrollbarWidth}px`);
          this.dialog.showModal();
        } catch (error) {
          document.body.style.removeProperty('--myart-cart-page-padding');
          this.showError('The cart could not open. Please use the cart page instead.');
          console.error('Cart dialog failed to open.', error);
          return false;
        }
      }

      document.body.classList.add('myart-cart-open');
      this.configureCartTrigger();
      const trigger = document.getElementById('cart-icon-bubble');
      trigger?.setAttribute('aria-expanded', 'true');

      if (!wasOpen || !this.dialog.contains(document.activeElement)) this.focusInitialControl();
      if ((!wasOpen || wasClosing) && !this.motionPreference?.matches && this.dialog.animate) {
        this.drawerMotion = this.dialog.animate([
          { transform: currentTransform && currentTransform !== 'none' ? currentTransform : 'translateX(100%)' }, { transform: 'translateX(0)' }
        ], { duration: 300, easing: 'cubic-bezier(0.2, 0, 0, 1)' });
      }

      if (refresh) {
        this.requestReconciliation();
      } else {
        this.scheduleEnhancements();
      }
      this.flushPromotionCelebration();
      return true;
    }

    updateLayoutWidth() {
      // The open production drawer occupies the viewport after scroll locking;
      // its right edge does not reserve the former page scrollbar's space.
      this.style.setProperty('--myart-cart-layout-width', `${Math.max(0, window.innerWidth)}px`);
    }

    focusInitialControl() {
      if (!this.dialog?.open) return;
      const close = this.dialog.querySelector('[data-cart-action="close"]');
      const title = this.dialog.querySelector('.myart-cart__title');
      const target = (this.preferCloseFocus ? close : title) || this.dialog;
      if (target === title && !title.hasAttribute('tabindex')) title.tabIndex = -1;
      target.focus({ preventScroll: true });
    }

    close() {
      this.consentResumePending = false;
      this.dismissRevision += 1;
      if (!this.dialog?.open || this.closing) return;
      this.closing = true;
      const dialog = this.dialog;
      dialog.setAttribute('data-cart-closing', '');
      const start = getComputedStyle(dialog).transform;
      this.drawerMotion?.cancel();
      if (this.motionPreference?.matches || !dialog.animate) { dialog.close(); return; }
      const animation = dialog.animate([
        { transform: start === 'none' ? 'translateX(0)' : start }, { transform: 'translateX(100%)' }
      ], { duration: 260, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' });
      this.drawerMotion = animation;
      // A throttled engine may finish painting without delivering onfinish.
      // Keep normal motion; only the still-current close may release the modal.
      const finishClose = () => {
        clearTimeout(closeDeadline);
        if (this.drawerMotion !== animation || !this.closing ||
            this.dialog !== dialog || !dialog.open || !this.connected) return;
        dialog.close();
        animation.cancel();
        this.drawerMotion = null;
      };
      const closeDeadline = setTimeout(finishClose, 600);
      animation.onfinish = finishClose;
    }

    onDialogClose() {
      if (this.dialog?.open) return;
      this.closing = false;
      this.dialog?.removeAttribute('data-cart-closing');
      this.cancelRailDrag();
      this.cancelPromotionCelebration();
      this.cancelCartMotion();
      document.body.classList.remove('myart-cart-open');
      document.body.style.removeProperty('--myart-cart-page-padding');
      this.closeSheet({ restoreFocus: false });
      const trigger = document.getElementById('cart-icon-bubble');
      trigger?.setAttribute('aria-expanded', 'false');
      if (this.consentPause) { this.consentPause = false; return; }
      const returnTarget = this.activeElement?.isConnected ? this.activeElement : trigger;
      this.activeElement = null;
      this.restoreClosedModalFocus(returnTarget, this.dialog);
    }

    restoreClosedModalFocus(returnTarget, closedDialog, focusOwner = closedDialog, nativeRestoredFocus = null) {
      const activeAtClose = document.activeElement;
      if (activeAtClose !== returnTarget && (!nativeRestoredFocus || activeAtClose !== nativeRestoredFocus) && activeAtClose !== document.body &&
          activeAtClose !== document.documentElement && !focusOwner?.contains(activeAtClose)) return;
      queueMicrotask(() => {
        if (!this.connected || closedDialog?.open || this.dialog?.open ||
            this.accessoryDialog?.open || this.getOpenSheet()) return;
        const active = document.activeElement;
        // Native close may already restore the opener. Preserve a subsequent
        // focus decision, including focus already handed to a foreground modal.
        if (active !== activeAtClose && active !== returnTarget &&
            active !== document.body && active !== document.documentElement) return;
        const painted = (element) => {
          if (!(element instanceof HTMLElement) || !element.isConnected ||
              !element.getClientRects().length) return false;
          for (let node = element; node; node = node.parentElement) {
            const style = getComputedStyle(node);
            if (node.hidden || node.inert || node.getAttribute('aria-hidden') === 'true' ||
                style.display === 'none' || style.visibility !== 'visible' ||
                Number(style.opacity) === 0) return false;
          }
          return true;
        };
        const hitVisible = (element, modal) => {
          if (!painted(element)) return false;
          const rect = element.getBoundingClientRect();
          const left = Math.max(0, rect.left), right = Math.min(window.innerWidth, rect.right);
          const top = Math.max(0, rect.top), bottom = Math.min(window.innerHeight, rect.bottom);
          if (right <= left || bottom <= top) return false;
          const hit = document.elementFromPoint((left + right) / 2, (top + bottom) / 2);
          return Boolean(hit && modal.contains(hit) && (element === hit || element.contains(hit)));
        };
        const modals = [...document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]')].reverse();
        for (const modal of modals) {
          if (this.contains(modal) || !painted(modal)) continue;
          if (modal.getAttribute('aria-modal') !== 'true') {
            // An open dialog created with show() is not a modal surface.
            try { if (!modal.matches(':modal')) continue; } catch (_) { continue; }
          }
          if (modal.contains(active) && hitVisible(active, modal)) return;
          for (const target of modal.querySelectorAll('button, a[href], input:not([type="hidden"]), select, textarea, [tabindex]')) {
            if (target.disabled || target.matches(':disabled') || target.tabIndex < 0 || !hitVisible(target, modal)) continue;
            const beforeFocus = document.activeElement;
            target.focus({ preventScroll: true });
            if (document.activeElement === target || document.activeElement !== beforeFocus) return;
          }
        }
        if (active !== activeAtClose && active !== returnTarget) return;
        if (painted(returnTarget) && !returnTarget.disabled) returnTarget.focus({ preventScroll: true });
      });
    }

    setActiveElement(element) {
      if (element instanceof HTMLElement) this.activeElement = element;
    }

    connectProductRail(rail, signal) {
      if (!rail || signal.aborted) return;
      const controller = { motionPreference: this.motionPreference,
        railFor: target => target instanceof Element && rail.contains(target) ? rail : null };
      for (const name of ['onRailWheel', 'onRailPointerDown', 'onRailPointerMove', 'endRailDrag', 'cancelRailDrag', 'onRailKeyDown']) {
        controller[name] = MyArtCartDrawer.prototype[name];
      }
      rail.style.setProperty('scroll-snap-type', 'inline mandatory', 'important');
      rail.style.scrollBehavior = 'auto';
      rail.style.touchAction = 'pan-x pan-y pinch-zoom';
      rail.addEventListener('wheel', event => controller.onRailWheel(event), {signal, passive: false});
      rail.addEventListener('pointerdown', event => controller.onRailPointerDown(event), {signal});
      document.addEventListener('pointermove', event => controller.onRailPointerMove(event), {signal});
      document.addEventListener('pointerup', event => controller.endRailDrag(event), {signal});
      document.addEventListener('pointercancel', () => controller.cancelRailDrag(), {signal});
      rail.addEventListener('keydown', event => controller.onRailKeyDown(event), {signal});
      rail.addEventListener('dragstart', event => event.preventDefault(), {signal});
      rail.addEventListener('click', event => {
        const suppressed = controller.railDragClick;
        controller.railDragClick = null;
        if (suppressed && suppressed.contains(event.target)) { event.preventDefault(); event.stopImmediatePropagation(); }
      }, {signal, capture: true});
      signal.addEventListener('abort', () => controller.cancelRailDrag(), {once: true});
    }

    railFor(target) {
      const rail = target instanceof Element ? target.closest('.myart-cart__rail') : null;
      return rail && this.contains(rail) ? rail : null;
    }

    onRailWheel(event) {
      const rail = this.railFor(event.target);
      // Horizontal/diagonal trackpad gestures and pinch zoom stay browser-owned.
      // Convert discrete vertical wheel steps; fine pixel deltas keep vertical scrolling.
      if (!rail || event.ctrlKey || event.metaKey || event.shiftKey || event.deltaX ||
          (!event.deltaMode && (!Number.isInteger(event.deltaY) || Math.abs(event.deltaY) < 40))) return;
      const delta = event.deltaY * (event.deltaMode === 1 ? 24 : event.deltaMode === 2 ? rail.clientWidth : 1);
      const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const left = rail.scrollLeft;
      if (!delta || !max || (delta < 0 && left <= 1) || (delta > 0 && left >= max - 1)) return;
      event.preventDefault();
      rail.scrollBy({ left: delta, behavior: this.motionPreference?.matches ? 'instant' : 'smooth' });
    }

    onRailPointerDown(event) {
      this.railDragClick = null;
      this.cancelRailDrag();
      const rail = this.railFor(event.target);
      if (!rail || event.pointerType !== 'mouse' || event.button !== 0 ||
          event.target.closest('a, button, input, select, textarea, label, [role="button"], [role="link"], [contenteditable="true"]') ||
          rail.scrollWidth <= rail.clientWidth + 1) return;
      this.railDrag = { rail, pointer: event.pointerId, x: event.clientX, y: event.clientY, left: rail.scrollLeft, active: false };
    }

    onRailPointerMove(event) {
      const drag = this.railDrag;
      if (!drag || drag.pointer !== event.pointerId) return;
      if (!event.buttons || !drag.rail.isConnected) { this.cancelRailDrag(); return; }
      const dx = event.clientX - drag.x;
      if (!drag.active) {
        if (Math.abs(dx) < 6) return;
        if (Math.abs(event.clientY - drag.y) > Math.abs(dx)) { this.cancelRailDrag(); return; }
        drag.active = true;
        drag.rail.classList.add('is-dragging');
        drag.rail.setPointerCapture?.(event.pointerId);
      }
      event.preventDefault();
      drag.rail.scrollLeft = Math.max(0, Math.min(drag.rail.scrollWidth - drag.rail.clientWidth, drag.left - dx));
    }

    endRailDrag(event) {
      if (this.railDrag?.pointer !== event.pointerId) return;
      if (this.railDrag.active) this.railDragClick = this.railDrag.rail;
      this.cancelRailDrag();
    }

    cancelRailDrag() {
      const drag = this.railDrag;
      this.railDrag = null;
      if (!drag) return;
      drag.rail.classList.remove('is-dragging');
      if (drag.rail.hasPointerCapture?.(drag.pointer)) drag.rail.releasePointerCapture(drag.pointer);
    }

    onRailKeyDown(event) {
      const rail = this.railFor(event.target);
      if (!rail || event.target !== rail || event.altKey || event.ctrlKey || event.metaKey) return;
      const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const step = (rail.firstElementChild?.getBoundingClientRect().width || 154) + 12;
      const targets = { ArrowLeft: rail.scrollLeft - step, ArrowRight: rail.scrollLeft + step, Home: 0, End: max };
      if (!(event.key in targets) || !max) return;
      event.preventDefault();
      rail.scrollTo({ left: Math.max(0, Math.min(max, targets[event.key])), behavior: this.motionPreference?.matches ? 'instant' : 'smooth' });
    }

    getSectionsToRender() {
      return SECTION_IDS.map((id) => ({ id }));
    }

    renderContents(state) {
      // Compatibility for callers outside the controller. Their response can
      // already be stale; only a fresh, reconciled cart may replace the view.
      return this.enqueueMutation({ operation: async () => {}, label: 'Cart updated.', open: true });
    }

    patchCartNode(target, source, preserved = new Set()) {
      if (preserved.has(target)) {
        // Keep loaded recommendation cards, but follow the server's empty/full
        // heading and rail semantics when their enclosing region changes.
        if (target.nodeType === 1 && target.hasAttribute('data-cart-bestsellers')) {
          for (const name of ['aria-labelledby', 'data-cart-recommendations']) {
            if (source.hasAttribute(name)) target.setAttribute(name, source.getAttribute(name));
            else target.removeAttribute(name);
          }
        }
        return;
      }
      if (target.nodeType !== 1) { if (target.nodeValue !== source.nodeValue) target.nodeValue = source.nodeValue; return; }
      for (const attr of [...target.attributes]) {
        if (attr.name === 'data-myart-pointer-focus' || attr.name === 'data-cart-pending') continue;
        if (!source.hasAttribute(attr.name)) target.removeAttribute(attr.name);
      }
      for (const attr of source.attributes) {
        if (target.getAttribute(attr.name) !== attr.value) target.setAttribute(attr.name, attr.value);
      }
      if (target instanceof HTMLInputElement) {
        if (target.value !== source.value) target.value = source.value;
        target.checked = source.checked;
      }
      // Conditional sections may share a tag but never share ownership. In
      // particular, a retained recommendations rail must not become accessories
      // when the last item disappears or the first item is added.
      const regionKey = node => {
        for (const name of ['data-cart-lines', 'data-cart-protection-warning', 'data-cart-empty',
          'data-cart-recommendations-section', 'data-cart-bestsellers', 'data-cart-together', 'data-cart-bundles-section']) {
          if (node.hasAttribute(name)) return `cart-region:${name}`;
        }
        if (node.nodeName !== 'SECTION') return '';
        if (node.classList.contains('myart-cart__promotion')) return 'cart-region:promotion';
        if (node.classList.contains('myart-cart__summary')) return 'cart-region:summary';
        if (node.getAttribute('aria-labelledby') === 'MyArtCartTogetherTitle') return 'cart-region:accessories';
        return '';
      };
      const key = node => node.nodeType === 1 ? ((node.hasAttribute('data-cart-key') ? `${node.nodeName}:${node.getAttribute('data-cart-key')}:${node.getAttribute('data-cart-action') || ''}` : '') || node.id || node.getAttribute('data-product-handle') || regionKey(node)) : '';
      const old = [...target.childNodes];
      const keyed = new Map(old.filter(node => key(node)).map(node => [key(node), node]));
      const used = new Set();
      // Discount recalculation can change a line key without changing the
      // merchandise. Keep its loaded media only for an unambiguous purchase
      // identity and paid/free role; never guess between personalized lines.
      const lineIdentity = node => {
        if (node.nodeType !== 1 || !node.hasAttribute('data-cart-line')) return '';
        const identity = this.lineIdentities.get(node.getAttribute('data-cart-key'));
        return identity ? JSON.stringify([identity, node.classList.contains('myart-cart__line--gift')]) : '';
      };
      const incoming = [...source.childNodes];
      const uniqueLine = node => {
        const identity = lineIdentity(node);
        if (!identity || incoming.filter(item => lineIdentity(item) === identity).length !== 1) return null;
        const matches = old.filter(item => lineIdentity(item) === identity);
        if (matches.length !== 1 || used.has(matches[0])) return null;
        // An exact incoming key owns its old node even if another row rekeys.
        if (incoming.some(item => key(item) === key(matches[0]))) return null;
        return matches[0];
      };
      let cursor = target.firstChild;
      for (const next of [...source.childNodes]) {
        const id = key(next);
        let current = id ? keyed.get(id) : old.find(node => !used.has(node) && !key(node) && node.nodeType === next.nodeType && node.nodeName === next.nodeName);
        if (!current) current = uniqueLine(next);
        if (current && current.nodeName !== next.nodeName) current = null;
        if (!current) current = next.cloneNode(true);
        else this.patchCartNode(current, next, preserved);
        used.add(current);
        if (current !== cursor) target.insertBefore(current, cursor);
        cursor = current.nextSibling;
      }
      old.forEach(node => { if (!used.has(node)) node.remove(); });
    }

    applySections(sections, { keepOpen = false, focusDescriptor = null, settlementPending = false } = {}) {
      this.cancelRailDrag();
      const drawerHtml = sections?.['cart-drawer'];
      if (typeof drawerHtml !== 'string') {
        this.requireCartReview();
        const receivedSections = sections && typeof sections === 'object'
          ? Object.entries(sections)
            .map(([id, html]) => `${id}:${html === null ? 'null' : typeof html}`)
            .join(', ') || 'none'
          : typeof sections;
        throw new Error(`Cart drawer section is missing. Received ${receivedSections}.`);
      }

      const drawerDocument = parseDocument(drawerHtml);
      const sourceDrawer = drawerDocument.querySelector('cart-drawer');
      const validDrawer = sourceDrawer?.classList.contains('myart-cart-host') &&
        this.matchesPreviewTheme(sourceDrawer) &&
        sourceDrawer.querySelector('dialog[data-cart-dialog]') && sourceDrawer.querySelector('[data-cart-view]') &&
        ['data-cart-url', 'data-cart-add-url', 'data-cart-change-url', 'data-cart-update-url'].every((name) => sourceDrawer.hasAttribute(name));
      if (!validDrawer) {
        this.requireCartReview();
        throw new Error('The cart preview changed. Select Check cart, or reload this page before continuing.');
      }

      // The old protection charge is expected only during this owned settlement.
      // Keep its server warning in the DOM for recovery, but out of the pending UI.
      // Ordinary reads, final rendering and errors always expose real mismatches.
      if (settlementPending) {
        for (const warning of sourceDrawer.querySelectorAll('[data-cart-protection-warning]')) {
          warning.hidden = true;
          warning.setAttribute('data-cart-protection-pending', '');
        }
      }
      const wasOpen = keepOpen && Boolean(this.dialog?.open);
      for (const attribute of [...this.attributes]) {
        if (attribute.name.startsWith('data-')) this.removeAttribute(attribute.name);
      }
      for (const attribute of [...sourceDrawer.attributes]) {
        if (attribute.name.startsWith('data-')) this.setAttribute(attribute.name, attribute.value);
      }
      // Keep the modal in the top layer. Recreating it resets scroll, focus and
      // its backdrop on every quantity change, even though the dialog is open.
      const currentView = this.querySelector('[data-cart-view]');
      const nextView = sourceDrawer.querySelector('[data-cart-view]');
      const motion = this.captureCartMotion();
      this.cancelCartMotion();
      const scrollTop = this.querySelector('[data-cart-scroll]')?.scrollTop || 0;
      const railOffsets = [...this.querySelectorAll('[data-cart-together], [data-cart-recommendations]')]
        .map((rail) => ({ selector: rail.hasAttribute('data-cart-together') ? '[data-cart-together]' : '[data-cart-recommendations]', left: rail.scrollLeft }));
      const productSignature = (root) => [...new Set([...root.querySelectorAll('[data-cart-product-id]')]
        .map((line) => line.dataset.cartProductId))].sort().join('|');
      const preserved = new Set();
      for (const selector of ['[data-cart-bestsellers]', '[data-cart-bundles-section]']) {
        const node = currentView?.querySelector(selector);
        if (node) preserved.add(node);
      }
      const recommendations = this.querySelector('[data-cart-recommendations-section]');
      const nextRecommendations = nextView.querySelector('[data-cart-recommendations-section]');
      if (recommendations && nextRecommendations && productSignature(currentView) === productSignature(nextView)) {
        preserved.add(recommendations);
      }
      if (currentView && this.dialog) {
        nextView.inert = Boolean(this.getOpenSheet());
        this.patchCartNode(currentView, nextView, preserved);
      } else {
        this.innerHTML = sourceDrawer.innerHTML;
      }
      this.renderVersion += 1;
      this.cacheDom();
      const scroll = this.querySelector('[data-cart-scroll]');
      if (scroll) scroll.scrollTop = scrollTop;
      for (const offset of railOffsets) {
        const rail = this.querySelector(offset.selector);
        if (rail) rail.scrollLeft = offset.left;
      }

      this.replaceCartIcon(sections?.['cart-icon-bubble']);
      this.configureCartTrigger();

      if (wasOpen && this.dialog && !this.dialog.open) {
        this.dialog.showModal();
        document.body.classList.add('myart-cart-open');
        document.getElementById('cart-icon-bubble')?.setAttribute('aria-expanded', 'true');
      }

      this.scheduleEnhancements();
      this.setBusy(this.pendingMutations > 0 || this.busyRefreshes > 0);
      this.syncRecommendationQuantities();
      if (!this.closing) this.animateCartUpdate(motion);
      if (!this.getOpenSheet()) {
        if (focusDescriptor) queueMicrotask(() => this.restoreFocus(focusDescriptor));
        else if (wasOpen && !this.dialog.contains(document.activeElement)) queueMicrotask(() => this.focusInitialControl());
      }
    }

    captureCartMotion() {
      if (!this.dialog?.open || this.motionPreference?.matches || this.getOpenSheet()) return null;
      const scroll = this.querySelector('[data-cart-scroll]');
      if (!scroll || typeof scroll.animate !== 'function') return null;
      const track = this.querySelector('.myart-cart__promotion-track');
      const fill = track?.querySelector('span');
      const width = track?.getBoundingClientRect().width || 0;
      // Read the painted frame, including an unfinished previous animation.
      // Do not derive motion from cart quantities or predict a discount.
      return {
        progress: width && fill ? fill.getBoundingClientRect().width / width : 0,
        // Following sections move with the rows, without animating the scroll
        // container's layout height or clipping already-visible merchandise.
        followers: [...scroll.children].filter(node =>
          this.querySelector('[data-cart-lines]')?.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING
        ).map(element => ({ element, top: element.getBoundingClientRect().top })),
        rows: [...this.querySelectorAll('[data-cart-line]')].map((row) => ({
          key: row.dataset.cartKey,
          identity: this.lineIdentities.get(row.dataset.cartKey),
          gift: row.classList.contains('myart-cart__line--gift'),
          top: row.getBoundingClientRect().top,
          opacity: getComputedStyle(row).opacity,
          clipPath: getComputedStyle(row).clipPath,
        })),
      };
    }

    cancelCartMotion() {
      this.cartAnimations.forEach((animation) => animation.cancel());
      this.cartAnimations.clear();
    }

    animateCartUpdate(previous) {
      if (!previous || !this.dialog?.open || this.motionPreference?.matches || this.getOpenSheet()) return;
      const plans = [];
      const ease = 'cubic-bezier(0.22, 1, 0.36, 1)';
      // Batch layout reads before starting effects. Only presentation is
      // animated; the new DOM, accessible values and prices are already final.
      const rows = [...this.querySelectorAll('[data-cart-line]')];
      for (const row of rows) {
        let before = previous.rows.find((item) => item.key === row.dataset.cartKey);
        const identity = this.lineIdentities.get(row.dataset.cartKey);
        const gift = row.classList.contains('myart-cart__line--gift');
        // A promotion can rekey the paid row. Reuse its position only when
        // purchase identity and paid/free role identify exactly one row.
        if (!before && identity) {
          const oldMatches = previous.rows.filter((item) => item.identity === identity && item.gift === gift);
          const newMatches = rows.filter((item) => this.lineIdentities.get(item.dataset.cartKey) === identity &&
            item.classList.contains('myart-cart__line--gift') === gift);
          if (oldMatches.length === 1 && newMatches.length === 1) before = oldMatches[0];
        }
        const offset = before ? before.top - row.getBoundingClientRect().top : 0;
        if (!before || Math.abs(offset) > 0.5 || Number(before.opacity) < 1) {
          plans.push({ element: row, frames: [
            { opacity: before?.opacity || 0, transform: `translateY(${offset}px)`,
              clipPath: before ? before.clipPath || 'inset(0)' : 'inset(0 0 100% 0)' },
            { opacity: 1, transform: 'translateY(0)', clipPath: 'inset(0)' },
          ], duration: 360 });
        }
      }
      for (const before of previous.followers || []) {
        if (!this.contains(before.element)) continue;
        const offset = before.top - before.element.getBoundingClientRect().top;
        if (Math.abs(offset) > 0.5) plans.push({ element: before.element, frames: [
          { transform: `translateY(${offset}px)` }, { transform: 'translateY(0)' },
        ], duration: 360 });
      }
      const track = this.querySelector('.myart-cart__promotion-track');
      const fill = track?.querySelector('span');
      const width = track?.getBoundingClientRect().width || 0;
      if (fill && width) {
        const start = Math.min(1, Math.max(0, previous.progress)) * width;
        const end = fill.getBoundingClientRect().width;
        if (Math.abs(start - end) > 0.5) plans.push({ element: fill,
          frames: [{ width: `${start}px` }, { width: `${end}px` }], duration: 560 });
      }
      for (const { element, frames, duration } of plans) {
        try {
          const animation = element.animate(frames, { duration, easing: ease });
          this.cartAnimations.add(animation);
          // Optional motion must not leave rows clipped if completion stalls.
          // Keep the original effect; the deadline removes only this animation.
          let deadline;
          const release = () => {
            clearTimeout(deadline);
            this.cartAnimations.delete(animation);
            animation.cancel();
          };
          animation.addEventListener('finish', release, { once: true });
          animation.addEventListener('cancel', () => {
            clearTimeout(deadline);
            this.cartAnimations.delete(animation);
          }, { once: true });
          deadline = setTimeout(release, duration + 400);
        } catch {
          // Optional visual enhancement must never interrupt a cart update.
        }
      }
    }

    replaceCartIcon(sectionHtml) {
      if (typeof sectionHtml !== 'string') return;
      const target = document.getElementById('cart-icon-bubble');
      if (!target) return;
      const sectionDocument = parseDocument(sectionHtml);
      const wrapper = sectionDocument.getElementById('shopify-section-cart-icon-bubble');
      target.innerHTML = wrapper ? wrapper.innerHTML : sectionDocument.body.innerHTML;
    }

    captureFocus() {
      const active = document.activeElement;
      if (!(active instanceof HTMLElement) || !this.contains(active)) return null;
      if (!active.dataset.cartFocus && !active.dataset.cartAction) return null;
      return {
        focus: active.dataset.cartFocus || active.dataset.cartAction || '',
        key: active.dataset.cartKey || active.closest('[data-cart-key]')?.dataset.cartKey || '',
        handle: active.dataset.productHandle || active.closest('[data-product-handle]')?.dataset.productHandle || '',
      };
    }

    restoreFocus(descriptor) {
      if (!descriptor || !this.dialog?.open) return;
      const candidates = [...this.querySelectorAll('[data-cart-focus], [data-cart-action]')].map((element) => ({
        element,
        focus: element.dataset.cartFocus || element.dataset.cartAction || '',
        key: element.dataset.cartKey || element.closest('[data-cart-key]')?.dataset.cartKey || '',
        handle: element.dataset.productHandle || element.closest('[data-product-handle]')?.dataset.productHandle || '',
      })).filter((candidate) => candidate.focus === descriptor.focus && candidate.handle === descriptor.handle);
      const exact = candidates.find((candidate) => candidate.key === descriptor.key)?.element;
      const identity = descriptor.key && this.lineIdentities.get(descriptor.key);
      const rekeyed = identity ? candidates.filter((candidate) => this.lineIdentities.get(candidate.key) === identity) : [];
      // Discounts can replace a key or split one purchase into several rows.
      // Continue on that purchase only when its control is unambiguous; never
      // put the next keyboard action on an unrelated item or an arbitrary split.
      const replacement = rekeyed.length === 1 ? rekeyed[0].element : null;
      const fallback = this.querySelector('[data-cart-action="close"]');
      (exact || replacement || fallback)?.focus({ preventScroll: true });
    }

    async refreshSections({ keepOpen = true, focusDescriptor = null, settlementPending = false } = {}) {
      this.refreshController?.abort();
      const bundled = this.bundledSections;
      this.bundledSections = null;
      if (bundled && bundled.writeRevision === this.writeRevision && bundled.readVersion === this.cartReadVersion && this.isConnected) {
        this.renderRequestVersion += 1;
        this.applySections(bundled.sections, { keepOpen, focusDescriptor, settlementPending });
        return true;
      }
      const controller = new AbortController();
      this.refreshController = controller;
      const requestSequence = ++this.renderRequestVersion;
      const requestVersion = this.renderVersion;
      const separator = (this.dataset.cartUrl || '/cart').includes('?') ? '&' : '?';
      const url = this.withThemeContext(`${this.dataset.cartUrl || '/cart'}${separator}sections=${SECTION_IDS.join(',')}`);
      const sections = await this.requestJson(url, {
        credentials: 'same-origin',
        cache: 'no-store',
        // `/cart` is content-negotiated by Shopify. An `Accept: application/json`
        // header turns this Section Rendering request into the Cart JSON API and
        // drops the requested section HTML entirely.
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: controller.signal,
      });
      if (requestSequence !== this.renderRequestVersion || controller.signal.aborted || requestVersion !== this.renderVersion || !this.isConnected) return false;
      this.applySections(sections, { keepOpen, focusDescriptor, settlementPending });
      return true;
    }

    matchesPreviewTheme(drawer) {
      const theme = window.Shopify?.theme;
      if (!['unpublished', 'development'].includes(String(theme?.role || '').toLowerCase())) return true;
      return String(drawer?.dataset.cartConfirmationTheme || '') === String(theme.id);
    }

    withThemeContext(value) {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('The cart preview route could not be confirmed.');
      const theme = window.Shopify?.theme;
      const role = String(theme?.role || '').toLowerCase();
      if (role === 'unpublished' || role === 'development') {
        const id = String(theme?.id || '');
        if (!/^\d+$/.test(id) || !Number.isSafeInteger(Number(id)) || Number(id) <= 0) {
          this.requireCartReview();
          throw new Error('The cart preview could not be confirmed. Please reload this page.');
        }
        // A preview cookie is shared between tabs. Pin section reads to the
        // theme that rendered this page, even if another preview changes it.
        url.searchParams.set('preview_theme_id', id);
      }
      return url.href;
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const checkout = target.closest('[data-cart-checkout], a[href$="/checkout"], a[href$="/checkout/"]');
      if (checkout) {
        event.preventDefault();
        this.checkout(checkout);
        return;
      }

      const sheet = target.closest('[data-cart-sheet]');
      if (sheet && target === sheet) {
        this.closeSheet();
        return;
      }

      const control = target.closest('[data-cart-action]');
      if (!(control instanceof HTMLElement) || control.hasAttribute('disabled')) return;
      const action = control.dataset.cartAction;

      if (action === 'close') {
        event.preventDefault();
        this.close();
        return;
      }
      if (action === 'close-sheet') {
        event.preventDefault();
        this.closeSheet();
        return;
      }
      if (action === 'view-photo') {
        event.preventDefault();
        this.openPhotoPreview(control);
        return;
      }
      if (action === 'retry') {
        event.preventDefault();
        this.requestReconciliation({ review: true });
        return;
      }
      if (action === 'open-cashback') {
        event.preventDefault();
        if (window.matchMedia('(min-width: 901px)').matches) return;
        this.openSheet('cashback', control);
        return;
      }
      if (action === 'increase' || action === 'decrease') {
        event.preventDefault();
        const line = control.closest('[data-cart-line]');
        const input = line?.querySelector('[data-cart-quantity]');
        if (!(input instanceof HTMLInputElement)) return;
        const step = Number.parseInt(input.step, 10) || 1;
        this.withControlFeedback(control, () => this.changeQuantity(control.dataset.cartKey, action === 'increase' ? step : -step, 'Quantity updated.'));
        return;
      }
      if (action === 'remove') {
        event.preventDefault();
        if (control.hasAttribute('data-cart-pending')) return;
        this.withControlFeedback(control, () => this.removeLine(control.dataset.cartKey));
        return;
      }
      if (action === 'increase-card' || action === 'decrease-card') {
        event.preventDefault();
        this.withControlFeedback(control, () => this.changeQuantity(control.dataset.cartKey, action === 'increase-card' ? 1 : -1, 'Cart updated.'));
        return;
      }
      if (action === 'add-product') {
        event.preventDefault();
        this.withControlFeedback(control, () => this.prepareProductAdd(control, {}));
        return;
      }
      if (action === 'add-bundle') {
        event.preventDefault();
        this.withControlFeedback(control, () => this.prepareProductAdd(control, { _bundle_id: control.dataset.bundleId || '', _bundle_instance: this.createOperationId() }));
      }
    }

    onChange(event) {
      const control = event.target;
      if (control instanceof HTMLInputElement && control.matches('[data-cart-quantity]')) {
        const min = Number.parseInt(control.min, 10) || 1;
        const max = control.max ? Number.parseInt(control.max, 10) : Infinity;
        const step = Number.parseInt(control.step, 10) || 1;
        const value = Number(control.value);
        if (!Number.isSafeInteger(value) || value < min || value > max || (value - min) % step !== 0) {
          control.value = control.defaultValue;
          control.setCustomValidity('Enter an available quantity.');
          control.reportValidity();
          control.setCustomValidity('');
          return;
        }
        const selection = this.captureLineSelection(control.dataset.cartKey, true);
        this.withControlFeedback(control, () => this.updateLine(control.dataset.cartKey, value, 'Quantity updated.', { selection }));
        return;
      }

      if (control instanceof HTMLInputElement && control.matches('[data-cart-protection]')) {
        this.withControlFeedback(control, () => this.enqueueMutation({
          label: control.checked ? 'Shipping Protection added.' : 'Shipping Protection removed.',
          protectionMode: control.checked,
          operation: async () => {},
        }));
        return;
      }

      if (control instanceof HTMLInputElement && control.closest('[data-product-sheet-form]')) {
        this.updateProductSheetVariant();
      }
    }

    onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-product-sheet-form]')) return;
      event.preventDefault();
      this.submitProductSheet();
    }

    createOperationId() {
      return window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    }

    lineIdentity(item) {
      const properties = Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b));
      return JSON.stringify([String(item.variant_id || item.id), String(item.selling_plan_allocation?.selling_plan?.id || ''), properties]);
    }

    identityQuantity(cart, identity) {
      return cart.items.reduce((total, item) => total + (this.lineIdentity(item) === identity ? Number(item.quantity) : 0), 0);
    }

    captureLineSelection(key, grouped = false) {
      const row = grouped ? [...this.querySelectorAll('[data-cart-line]')].find((node) => node.dataset.cartKey === key) : null;
      let members = [[key, null]];
      try { if (row?.dataset.cartMembers) members = JSON.parse(row.dataset.cartMembers); } catch { /* Fresh-key fallback below. */ }
      if (!Array.isArray(members) || members.some((entry) => !Array.isArray(entry) || typeof entry[0] !== 'string' ||
          (entry[1] !== null && (!Number.isSafeInteger(entry[1]) || entry[1] <= 0)))) return null;
      const items = members.map(([member, quantity]) => {
        const item = this.lastCart?.items.find((entry) => entry.key === member) || this.lineSnapshots.get(member);
        return item ? { ...item, quantity: quantity ?? item.quantity }
          : quantity !== null ? { key: member, quantity, unresolved: true } : null;
      });
      if (items.some((item) => !item)) return null;
      // Freeze what was selected before any queued operation reads/rekeys it.
      return items.map((item) => ({ ...item, identity: item.unresolved ? null : this.lineIdentity(item) }));
    }

    selectionUpdates(cart, selectedItems, quantity = 0) {
      const selected = [...new Map(selectedItems.map((item) => {
        if (!item.unresolved) return [item.key, item];
        const exact = cart.items.find((entry) => entry.key === item.key);
        if (!exact) throw new Error('This item changed. Review the updated cart and try again.');
        return [item.key, { ...exact, quantity: item.quantity }];
      })).values()];
      const identities = new Set(selected.map((item) => item.identity || this.lineIdentity(item)));
      if (identities.size !== 1) throw new Error('This item changed. Review the updated cart and try again.');
      const identity = [...identities][0];
      const matches = cart.items.filter((item) => this.lineIdentity(item) === identity);
      const keys = new Set(selected.map((item) => item.key));
      if (matches.length > 1 && !matches.some((item) => keys.has(item.key))) {
        throw new Error('This item changed in another window. Review the updated cart and try again.');
      }
      const selectedQuantity = selected.reduce((sum, item) => sum + Number(item.quantity), 0);
      let remaining = selectedQuantity - quantity;
      if (!matches.length || (remaining > this.identityQuantity(cart, identity))) {
        throw new Error('This item changed in another window. Review the updated cart and try again.');
      }
      // A merged line can contain unselected units. A key is an address, never
      // authority to delete its entire *new* quantity. Prefer surviving keys.
      const ordered = [...matches].sort((a, b) => Number(keys.has(b.key)) - Number(keys.has(a.key)));
      const updates = {};
      for (const item of ordered) {
        if (!remaining) break;
        const removed = remaining < 0 ? remaining : Math.min(remaining, Number(item.quantity));
        const next = Number(item.quantity) - removed;
        if (next === 0 ? item.instructions?.can_remove === false : item.instructions?.can_update_quantity === false) {
          throw new Error('This option follows its painting and cannot be changed separately.');
        }
        const rule = item.quantity_rule || {};
        if (next && (next < Number(rule.min || 1) || (rule.max != null && next > Number(rule.max)) ||
            (next - Number(rule.min || 1)) % Number(rule.increment || 1))) {
          throw new Error('This quantity is unavailable. Choose a quantity shown for this item.');
        }
        if (selected.some((source) => source.parent_relationship) && !keys.has(item.key)) {
          throw new Error('This option changed with its painting. Review the updated cart.');
        }
        updates[item.key] = next;
        remaining -= removed;
      }
      return { updates, identity, expected: this.identityQuantity(cart, identity) - selectedQuantity + quantity };
    }

    quantityContract(cart, updates, additions = []) {
      const removed = new Set(Object.keys(updates).filter((key) => updates[key] === 0));
      // Native nested services disappear with their exact parent, recursively.
      for (let pass = 0; pass < cart.items.length; pass += 1) {
        let changed = false;
        for (const item of cart.items) {
          const parent = item.parent_relationship?.parent_key || item.parent_relationship?.parent?.key;
          if (parent && removed.has(parent) && !removed.has(item.key)) { removed.add(item.key); changed = true; }
        }
        if (!changed) break;
      }
      const expected = new Map();
      for (const item of cart.items) {
        const identity = this.lineIdentity(item);
        const quantity = removed.has(item.key) ? 0 : (updates[item.key] ?? item.quantity);
        expected.set(identity, (expected.get(identity) || 0) + Number(quantity));
      }
      for (const item of additions) {
        const identity = this.lineIdentity(item);
        expected.set(identity, (expected.get(identity) || 0) + Number(item.quantity));
      }
      return (actual) => actual.items.every((item) => expected.has(this.lineIdentity(item))) &&
        [...expected].every(([identity, quantity]) => this.identityQuantity(actual, identity) === quantity);
    }

    resolveLine(cart, key, { deltaAnchor = false } = {}) {
      const exact = cart.items.find((item) => item.key === key);
      if (exact) return exact;
      const identity = this.lineIdentities.get(key);
      const matches = identity ? cart.items.filter((item) => this.lineIdentity(item) === identity) : [];
      // Discounts can change keys; only an unambiguous same-property/selling-plan
      // line is a safe replacement. Never use a stale positional line number.
      if (matches.length === 1) return matches[0];
      if (deltaAnchor && matches.length > 1) {
        const unitPrice = Number(matches[0].original_price ?? matches[0].price);
        const hasDiscount = matches.some((item) => Number(item.total_discount || 0) > 0 ||
          (item.line_level_discount_allocations || []).some((allocation) => Number(allocation.amount) > 0));
        if (Number.isFinite(unitPrice) && hasDiscount && matches.every((item) =>
          Number(item.original_price ?? item.price) === unitPrice)) {
          // A +/-1 intent changes the quantity of the same merchandise even if
          // Shopify has just split it into paid/free rows. A current key is only
          // the mutation anchor; the complete identity quantity is verified.
          return matches.reduce((largest, item) => item.quantity > largest.quantity ? item : largest);
        }
      }
      throw new Error('This item changed in another window. Review the updated cart and try again.');
    }

    updateLine(key, quantity, label = 'Quantity updated.', options = {}) {
      if (label && typeof label === 'object') { options = label; label = 'Quantity updated.'; }
      if (!key || !Number.isInteger(quantity) || quantity < 0) return Promise.resolve({ ok: false });
      const selection = options.selection || this.captureLineSelection(key) ||
        (Number.isSafeInteger(options.sourceQuantity) && options.sourceQuantity > 0
          ? [{ key, quantity: options.sourceQuantity, unresolved: true }] : null);
      return this.enqueueMutation({
        ...options, label,
        operation: async () => {
          const cart = await this.getCart();
          if (selection && !selection.some((item) => item.properties?._bundle_instance)) {
            const plan = this.selectionUpdates(cart, selection, quantity);
            if (!Object.keys(plan.updates).length) return;
            const entries = Object.entries(plan.updates);
            const increase = entries.find(([member, next]) => next > cart.items.find((item) => item.key === member).quantity);
            const url = increase ? this.dataset.cartChangeUrl : this.dataset.cartUpdateUrl;
            const body = increase ? { id: increase[0], quantity: increase[1] } : { updates: plan.updates };
            await this.confirmedWrite(url, body, this.quantityContract(cart, plan.updates));
            return;
          }
          const item = this.resolveLine(cart, key);
          await this.changeItem(cart, item, quantity);
        },
      });
    }

    changeQuantity(key, delta, label = 'Quantity updated.', options = {}) {
      if (!key || !Number.isInteger(delta) || delta === 0) return Promise.resolve({ ok: false });
      const pending = this.pendingQuantity;
      const plain = Object.keys(options).length === 0;
      if (!this.checkoutPending && plain && pending?.plain && pending.key === key && pending.delta === delta && pending.label === label) {
        pending.count += 1;
        return pending.promise;
      }
      const job = { key, delta, label, plain, count: 1 };
      job.promise = this.enqueueMutation({
        ...options, label,
        operation: async () => {
          let cart = await this.getCart();
          // No debounce delay. Accept adjacent taps until the baseline arrives;
          // taps during a write belong to the next job. Other actions are barriers.
          if (this.pendingQuantity === job) this.pendingQuantity = null;
          let remaining = job.count;
          while (remaining > 0) {
            const item = this.resolveLine(cart, key, { deltaAnchor: Math.abs(delta) === 1 });
            const rule = item.quantity_rule || {};
            const minimum = Number(rule.min || 1);
            const combined = Number(item.quantity) + delta * remaining;
            const singleLine = cart.items.filter((line) => this.lineIdentity(line) === this.lineIdentity(item)).length === 1;
            // Collapsing valid steps on one line is equivalent to their final
            // quantity. Split discounts and bounds retain individual fresh steps.
            const combine = singleLine && combined >= minimum &&
              (rule.max == null || combined <= Number(rule.max)) &&
              delta % Number(rule.increment || 1) === 0 &&
              (combined - minimum) % Number(rule.increment || 1) === 0;
            let quantity = Number(item.quantity) + delta * (combine ? remaining : 1);
            if (quantity < minimum) quantity = 0;
            await this.changeItem(cart, item, quantity);
            remaining -= combine ? remaining : 1;
            if (remaining) cart = await this.getCart();
          }
        },
      });
      this.pendingQuantity = job;
      void job.promise.finally(() => { if (this.pendingQuantity === job) this.pendingQuantity = null; });
      return job.promise;
    }

    async changeItem(cart, item, quantity) {
      if (quantity === 0 ? item.instructions?.can_remove === false : item.instructions?.can_update_quantity === false) {
        throw new Error('This option follows its painting and cannot be changed separately.');
      }
      if (quantity === 0 && item.properties?._bundle_instance) {
        return this.removeCartItem(cart, item);
      }
      const rule = item.quantity_rule || {};
      if (quantity !== 0 && (quantity < Number(rule.min || 1) ||
          (rule.max != null && quantity > Number(rule.max)) ||
          (quantity - Number(rule.min || 1)) % Number(rule.increment || 1) !== 0)) {
        throw new Error('This quantity is unavailable. Choose a quantity shown for this item.');
      }
      await this.confirmedWrite(this.dataset.cartChangeUrl, { id: item.key, quantity },
        this.quantityContract(cart, { [item.key]: quantity }));
    }

    removeLine(key) {
      if (!key) return Promise.resolve({ ok: false });
      // A repeated tap is the same intent, not a second removal after rekeying.
      if (this.removalIntents.has(key)) return this.removalIntents.get(key);
      const selection = this.captureLineSelection(key, true);
      const pending = !this.checkoutPending && this.pendingRemoval;
      if (pending) {
        pending.keys.add(key);
        pending.selections.set(key, selection);
        this.removalIntents.set(key, pending.promise);
        return pending.promise;
      }
      const job = { keys: new Set([key]), selections: new Map([[key, selection]]) };
      job.promise = this.enqueueMutation({
        label: 'Selected items removed.',
        operation: async () => {
          const cart = await this.getCart();
          // Freeze only when a fresh locked baseline arrives. Taps during the
          // write collect in the next batch; any different action is a barrier.
          if (this.pendingRemoval === job) this.pendingRemoval = null;
          const selected = [...job.keys].flatMap((requestedKey) => job.selections.get(requestedKey) || [this.resolveLine(cart, requestedKey)]);
          await this.removeSelectedQuantities(cart, selected);
        },
      });
      this.pendingRemoval = job;
      this.removalIntents.set(key, job.promise);
      void job.promise.finally(() => {
        if (this.pendingRemoval === job) this.pendingRemoval = null;
        for (const requestedKey of job.keys) {
          if (this.removalIntents.get(requestedKey) === job.promise) this.removalIntents.delete(requestedKey);
        }
      });
      return job.promise;
    }

    async removeSelectedQuantities(cart, selectedItems) {
      const unique = new Map();
      for (const source of selectedItems) {
        const exact = source.unresolved ? cart.items.find((item) => item.key === source.key) : source;
        if (!exact) throw new Error('This item changed. Review the updated cart and try again.');
        const selected = source.unresolved ? { ...exact, quantity: source.quantity } : source;
        if (selected.instructions?.can_remove === false) throw new Error('This option follows its painting and cannot be removed separately.');
        const instance = selected.properties?._bundle_instance;
        const members = instance ? cart.items.filter((item) => item.properties?._bundle_instance === instance) : [selected];
        for (const item of members) unique.set(item.key, item);
      }
      const groups = new Map();
      for (const item of unique.values()) {
        const identity = item.identity || this.lineIdentity(item);
        if (!groups.has(identity)) groups.set(identity, []);
        groups.get(identity).push(item);
      }
      const plans = [...groups.values()].map((items) => this.selectionUpdates(cart, items));
      const updates = Object.assign({}, ...plans.map((plan) => plan.updates));
      return this.confirmedWrite(this.dataset.cartUpdateUrl, { updates }, this.quantityContract(cart, updates));
    }

    async removeCartItem(cart, selected) {
      return this.removeCartItems(cart, [selected]);
    }

    async removeCartItems(cart, selectedItems) {
      const selectedByKey = new Map();
      for (const selected of selectedItems) {
        if (selected.instructions?.can_remove === false) {
          throw new Error('This option follows its painting and cannot be removed separately.');
        }
        const instance = selected.properties?._bundle_instance;
        const members = instance ? cart.items.filter((item) => item.properties?._bundle_instance === instance) : [selected];
        for (const item of members) selectedByKey.set(item.key, item);
      }
      const items = [...selectedByKey.values()];
      const updates = Object.fromEntries(items.map((item) => [item.key, 0]));
      return this.confirmedWrite(this.dataset.cartUpdateUrl, { updates }, this.quantityContract(cart, updates));
    }

    addFromWishlist(control) {
      const dismissRevision = this.dismissRevision;
      const job = this.prepareProductAdd(control, {}, { standalone: true });
      // Use the existing confirmed cart milestone, with no extra write or read.
      void Promise.resolve(this.whenMerchandiseConfirmed(job)).then((result) => {
        if (result?.ok && !result.options && this.isConnected && dismissRevision === this.dismissRevision) {
          this.open(control, { refresh: false });
        }
      });
      return job;
    }

    prepareProductAdd(control, properties, { standalone = false } = {}) {
      const handle = String(control.dataset.productHandle || '').trim();
      const variantId = Number(control.dataset.variantId);
      if (!handle && !variantId) return;
      if (control.dataset.needsOptions === 'true' || !variantId) {
        return this.openProductSheet(handle, properties, control, { standalone });
      } else {
        return this.addVariant(variantId, properties, 'Product added.');
      }
    }

    addVariant(variantId, properties = {}, label = 'Product added.') {
      if (!Number.isFinite(Number(variantId))) return Promise.resolve({ ok: false });
      const items = [{ id: Number(variantId), quantity: 1, properties }];
      return this.enqueueMutation({ label, operation: () => this.addItems(items) });
    }

    async addItems(items) {
      const before = await this.getCart();
      const additions = items.map((item) => ({
        variant_id: Number(item.id), quantity: Number(item.quantity || 1), properties: item.properties || {},
        selling_plan_allocation: item.selling_plan ? { selling_plan: { id: Number(item.selling_plan) } } : null,
      }));
      return this.confirmedWrite(this.dataset.cartAddUrl, { items }, this.quantityContract(before, {}, additions));
    }

    onProductFormSubmit(event) {
      const form = event.target;
      if (event.defaultPrevented || !this.isConnected || !(form instanceof HTMLFormElement) ||
          form.dataset.myartCartProductForm !== 'true' || form.closest('product-form, cart-drawer') ||
          String(form.method).toLowerCase() !== 'post' || event.submitter?.name === 'checkout') return false;
      try {
        const action = new URL(event.submitter?.getAttribute('formaction') || form.action, window.location.href);
        const expected = new URL(this.dataset.cartAddUrl, window.location.origin);
        const pathname = (url) => url.pathname.replace(/\/$/, '').replace(/\.js$/, '');
        if (action.origin !== window.location.origin || expected.origin !== action.origin ||
            pathname(action) !== pathname(expected) ||
            (event.submitter?.getAttribute('formmethod') || form.method).toLowerCase() !== 'post') return false;
      } catch { return false; }

      // Only these explicitly opted-in legacy product forms use this owner.
      // Photo upload, native product-form and checkout retain their own flows.
      event.preventDefault();
      if (form.dataset.cartSubmitPending === 'true' || event.submitter?.disabled) return true;
      const active = document.activeElement;
      const trigger = active instanceof HTMLElement && active.closest('.totalCardBar__item-buy')
        ? active : event.submitter || form.querySelector('[name="add"]');
      void this.submitProductForm(form, trigger);
      return true;
    }

    async submitProductForm(form, trigger) {
      const buttons = [...form.querySelectorAll('button[type="submit"], input[type="submit"]')];
      const previous = buttons.map((button) => ({ button, busy: button.getAttribute('aria-busy'),
        disabled: button.getAttribute('aria-disabled'), loading: button.classList.contains('loading') }));
      const formBusy = form.getAttribute('aria-busy');
      form.dataset.cartSubmitPending = 'true';
      try {
        const payload = new FormData(form);
        form.setAttribute('aria-busy', 'true');
        for (const { button } of previous) {
          button.setAttribute('aria-busy', 'true');
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('loading');
        }
        const result = await this.whenMerchandiseConfirmed(this.addFromForm(payload, { open: true, trigger }));
        if (!result?.ok && this.isConnected) {
          this.open(trigger, { refresh: false });
          this.showError(result?.error?.message || 'The product could not be added. Please check your cart before trying again.');
        }
      } catch (error) {
        // An uncertain add must never fall through to a second native POST.
        this.open(trigger, { refresh: false });
        this.handleOperationError(error);
      } finally {
        delete form.dataset.cartSubmitPending;
        if (formBusy === null) form.removeAttribute('aria-busy');
        else form.setAttribute('aria-busy', formBusy);
        for (const { button, busy, disabled, loading } of previous) {
          if (busy === null) button.removeAttribute('aria-busy');
          else button.setAttribute('aria-busy', busy);
          if (disabled === null) button.removeAttribute('aria-disabled');
          else button.setAttribute('aria-disabled', disabled);
          button.classList.toggle('loading', loading);
          // Native disabled belongs to variant availability; never overwrite it.
        }
      }
    }

    addFromForm(formData, { open = true, trigger } = {}) {
      const payload = new FormData();
      for (const [key, value] of formData.entries()) {
        if (key !== 'sections' && key !== 'sections_url') payload.append(key, value);
      }
      if (trigger) this.setActiveElement(trigger);
      return this.enqueueMutation({
        label: 'Product added.', open,
        operation: async () => {
          const before = await this.getCart();
          const id = Number(payload.get('id'));
          const quantity = Number(payload.get('quantity') || 1);
          const plan = String(payload.get('selling_plan') || '');
          const properties = {};
          let hasFile = false;
          for (const [key, value] of payload.entries()) {
            const match = /^properties\[(.*)\]$/.exec(key);
            if (match && typeof value === 'string') properties[match[1]] = value;
            if (typeof value !== 'string') hasFile = true;
          }
          const addition = {
            variant_id: id, quantity, properties,
            selling_plan_allocation: plan ? { selling_plan: { id: Number(plan) } } : null,
          };
          const matches = this.quantityContract(before, {}, [addition]);
          await this.confirmedWrite(this.dataset.cartAddUrl, payload, (after, response) => {
            if (!(id > 0 && quantity > 0)) return false;
            if (!hasFile) return matches(after);
            // A valid upload is represented by Shopify's returned property URL
            // and line key. Preserve the raw File payload; never guess that URL
            // or confirm an unrelated personalized line by variant count alone.
            const returned = response?.key ? [response] : response?.items || [];
            return returned.some((added) => {
              if (!added.key || Number(added.variant_id || added.id) !== id) return false;
              // The returned upload URL identifies the merchandise even when
              // a native discount has already replaced/split its response key.
              return this.quantityContract(before, {}, [{ ...added, quantity }])(after);
            });
          });
        },
      });
    }

    updateNote(note) {
      if (this.checkoutPending) return Promise.resolve({ ok: false, error: new Error('Checkout is being prepared. Please wait.') });
      if (this.pendingNote) {
        this.pendingNote.value = String(note || '');
        this.pendingNote.schedule();
        return this.pendingNote.promise;
      }
      const job = { value: String(note || ''), ready: false };
      const ready = new Promise((resolve) => {
        job.flush = () => {
          if (job.ready) return;
          job.ready = true;
          clearTimeout(job.delay);
          clearTimeout(job.deadline);
          resolve();
        };
        job.schedule = () => {
          if (job.ready) return;
          clearTimeout(job.delay);
          job.delay = setTimeout(job.flush, 300);
        };
        job.deadline = setTimeout(job.flush, 1200);
        job.schedule();
      });
      this.pendingNote = job;
      job.promise = this.enqueueMutation({
        label: 'Order note saved.', open: false,
        operation: async () => {
          await ready;
          if (this.pendingNote === job) this.pendingNote = null;
          const value = job.value;
          return this.confirmedWrite(this.dataset.cartUpdateUrl, { note: value },
            (cart) => String(cart.note || '') === value);
        },
      }).finally(() => {
        job.flush();
        if (this.pendingNote === job) this.pendingNote = null;
      });
      return job.promise;
    }

    setAddonVariantEnabled(variantId, enabled) {
      const id = Number(variantId);
      if (!Number.isInteger(id) || id <= 0) return Promise.resolve({ ok: false });
      return this.enqueueMutation({
        label: enabled ? 'Add-on selected.' : 'Add-on removed.', open: false,
        operation: async () => {
          const cart = await this.getCart();
          const related = cart.items.filter((item) => Number(item.variant_id) === id);
          if (related.some((item) => Object.keys(item.properties || {}).length || item.selling_plan_allocation)) {
            throw new Error('This add-on has personalized options in your cart. Review that item in the cart before changing it.');
          }
          if (enabled) {
            if (!related.length) {
              const items = [{ id, quantity: 1 }];
              const additions = [{ variant_id: id, quantity: 1, properties: {}, selling_plan_allocation: null }];
              await this.confirmedWrite(this.dataset.cartAddUrl, { items }, this.quantityContract(cart, {}, additions));
            }
            return;
          }
          if (!related.length) return;
          const updates = Object.fromEntries(related.map((item) => [item.key, 0]));
          const matches = this.quantityContract(cart, updates);
          await this.confirmedWrite(this.dataset.cartUpdateUrl, { updates }, (actual) =>
            matches(actual) && !actual.items.some((item) => Number(item.variant_id) === id));
        },
      });
    }

    requireCartReview() {
      const alreadyRequired = this.needsReview;
      this.needsReview = true;
      // Origin-scoped safety bit only: no cart token, customer or upload data.
      // No expiry can silently turn an unresolved write into permission to pay.
      try { window.localStorage.setItem(CART_REVIEW_KEY, '1'); } catch { /* Local recovery still works when shared storage is unavailable. */ }
      try { if (!alreadyRequired) this.channel?.postMessage({ reviewRequired: true }); } catch { /* Storage is authoritative across cooperating tabs. */ }
    }

    assertCartReady() {
      try { if (window.localStorage.getItem(CART_REVIEW_KEY) === '1') this.needsReview = true; } catch { /* Retain the in-memory gate. */ }
      if (!this.needsReview) return;
      const error = new Error('The last change could not be confirmed. Review the cart and select Check cart before continuing.');
      error.cartReviewRequired = true;
      this.showError(error.message);
      this.setBusy(this.pendingMutations > 0);
      throw error;
    }

    clearCartReview() {
      try { window.localStorage.removeItem(CART_REVIEW_KEY); } catch { /* A blocked store must not break local-only review. */ }
      let shared = false;
      try { shared = window.localStorage.getItem(CART_REVIEW_KEY) === '1'; } catch { /* In-memory review remains available. */ }
      if (shared) throw new Error('Your browser could not save the cart check. Reload this page and check your cart again.');
      this.needsReview = false;
    }

    beginExternalAdd(requestId) {
      if (!this.isConnected || typeof requestId !== 'string' || !/^[a-zA-Z0-9-]{16,80}$/.test(requestId)) return null;
      const token = {};
      this.externalAdditions.set(token, { requestId, freeKits: Number(this.dataset.cartFreeKitCount || 0), expires: Date.now() + 120000 });
      return token;
    }

    markExternalAddUncertain(token) {
      // Issued tokens may outlive their presentation deadline while a suspended
      // upload settles. This hook only locks; explicit fresh review unlocks.
      if (!token || typeof token !== 'object' || !this.externalAdditions.has(token)) return false;
      this.requireCartReview();
      this.showError('We could not confirm your photo upload. Select Check cart and review the items before continuing.');
      this.setBusy(this.pendingMutations > 0);
      return true;
    }

    cancelExternalAdd(token) {
      if (token && typeof token === 'object') this.externalAdditions.delete(token);
    }

    async confirmExternalAdd(token) {
      const entry = token && typeof token === 'object' ? this.externalAdditions.get(token) : null;
      this.cancelExternalAdd(token);
      try {
        if (!entry || entry.expires < Date.now() || !this.isConnected) {
          this.open(undefined, { refresh: true });
          return { ok: false };
        }
        return await this.enqueueMutation({
          open: true, label: 'Your painting was added.', externalFreeKitsBefore: entry.freeKits,
          operation: async () => {
            const cart = await this.getCart();
            if (!cart.items.some((item) => item.properties?.['_PYP request'] === entry.requestId && safePhotoUrl(item.properties?.Photo) && item.quantity > 0)) {
              const error = new Error('Your painting could not be confirmed. Check the cart before continuing.');
              error.uncertain = true;
              throw error;
            }
          },
        });
      } catch {
        // An optional handoff must never turn a saved upload into another POST.
        try { this.open(undefined, { refresh: true }); } catch { /* Caller retains its native cart fallback. */ }
        return { ok: false };
      }
    }

    // Existing callers still receive full settlement. UI adapters opt into the
    // separately confirmed merchandise milestone; late failures remain owned here.
    whenMerchandiseConfirmed(job) {
      return job?.merchandiseConfirmed || job;
    }

    enqueueMutation({ operation, label = 'Cart updated.', protectionMode = 'auto', open = false, allowReview = false, checkout = false, externalFreeKitsBefore = null }) {
      this.pendingQuantity = null;
      this.pendingRemoval = null;
      if (this.checkoutPending && !checkout) {
        return Promise.resolve({ ok: false, error: new Error('Checkout is being prepared. Please wait.') });
      }
      const focusDescriptor = this.captureFocus();
      const dismissRevision = this.dismissRevision;
      this.pendingMutations += 1;
      this.setBusy(true);
      this.refreshController?.abort();
      let confirmedWriteBefore;
      let operationCompleted = false;
      let merchandisePresented = false;
      let resolveMerchandise;
      const merchandiseConfirmed = new Promise((resolve) => { resolveMerchandise = resolve; });
      const result = this.mutationChain
        .catch(() => {})
        .then(() => this.withCartLock(async () => {
          try {
            if (!this.isConnected) return { ok: false };
            if (!allowReview) this.assertCartReady();
            this.clearError();
            this.pendingNotice = '';
            const writeRevision = this.writeRevision;
            confirmedWriteBefore = this.confirmedWriteRevision;
            const readVersion = this.cartReadVersion;
            const freeKitsBefore = externalFreeKitsBefore ?? Number(this.dataset.cartFreeKitCount || 0);
            await operation();
            operationCompleted = true;
            // Reuse only a read made by this operation, after its latest write.
            // A different queued intent always obtains its own current baseline.
            const verifiedCart = this.cartReadVersion > readVersion && this.cartReadWriteRevision === this.writeRevision ? this.lastCart : null;
            const protectionHandle = String(this.dataset.protectionHandle || '').trim();
            const hasConfirmedMerchandise = this.confirmedWriteRevision > confirmedWriteBefore || externalFreeKitsBefore !== null;
            // Keep the same writer/lock until the charge and total are verified.
            // Only presentation can complete early; no speculative cart state.
            if (protectionMode === 'auto' && !allowReview && !checkout && hasConfirmedMerchandise && verifiedCart &&
                protectionHandle && verifiedCart.items.some((item) => item.handle === protectionHandle)) {
              const presented = await this.refreshSections({ keepOpen: true, focusDescriptor, settlementPending: true });
              if (presented === false || !this.isConnected) throw new Error('The cart view changed. Review your saved items.');
              if (open && dismissRevision === this.dismissRevision) this.open(undefined, { refresh: false });
              this.syncLegacyAccessories(verifiedCart);
              this.announce(label);
              merchandisePresented = true;
              resolveMerchandise({ ok: true, cart: verifiedCart, settlementPending: true });
            }
            const cart = await this.synchronizeProtection(protectionMode, verifiedCart, { fresh: checkout || allowReview || protectionMode !== 'auto' });
            await this.refreshSections({ keepOpen: true, focusDescriptor });
            if (allowReview) this.clearCartReview();
            if (!merchandisePresented && open && dismissRevision === this.dismissRevision) this.open(undefined, { refresh: false });
            const freeKitsAfter = Number(this.dataset.cartFreeKitCount || 0);
            if (freeKitsAfter < this.pendingPromotionCelebration) this.pendingPromotionCelebration = 0;
            if ((this.writeRevision !== writeRevision || externalFreeKitsBefore !== null) && !allowReview && !checkout && freeKitsAfter > freeKitsBefore) {
              this.pendingPromotionCelebration = freeKitsAfter;
              this.flushPromotionCelebration();
            }
            this.announce(this.pendingNotice || label);
            this.notifyCart(cart, { broadcast: this.writeRevision !== writeRevision || externalFreeKitsBefore !== null });
            return { ok: true, cart };
          } catch (error) {
            const savedChange = (confirmedWriteBefore !== undefined && this.confirmedWriteRevision > confirmedWriteBefore) ||
              (operationCompleted && externalFreeKitsBefore !== null);
            if (error.uncertain || savedChange) this.requireCartReview();
            throw error;
          }
        }))
        .catch(async (error) => {
          this.pendingPromotionCelebration = 0;
          // A failed recovery read must not leave a genuine warning hidden.
          for (const warning of this.querySelectorAll('[data-cart-protection-pending]')) {
            warning.hidden = false;
            warning.removeAttribute('data-cart-protection-pending');
          }
          const savedChange = (confirmedWriteBefore !== undefined && this.confirmedWriteRevision > confirmedWriteBefore) ||
            (operationCompleted && externalFreeKitsBefore !== null);
          if (savedChange && !error.uncertain) {
            const savedError = new Error('Your cart change was saved, but the updated cart could not be shown. Select Check cart and review the items before trying again.');
            savedError.cause = error;
            savedError.saved = true;
            error = savedError;
          }
          try {
            await this.refreshSections({ keepOpen: true, focusDescriptor });
          } catch { /* Keep the last confirmed view with a visible recovery action. */ }
          if (open && dismissRevision === this.dismissRevision) this.open(undefined, { refresh: false });
          this.handleOperationError(error);
          return { ok: false, error };
        })
        .finally(() => {
          this.pendingMutations = Math.max(0, this.pendingMutations - 1);
          if (this.pendingMutations === 0) this.setBusy(false);
        });
      result.merchandiseConfirmed = merchandiseConfirmed;
      // Also settle the milestone on no-protection/no-write/error paths.
      void result.then(resolveMerchandise);
      this.mutationChain = result;
      return result;
    }

    async withCartLock(operation) {
      if (!window.navigator?.locks?.request) return operation();
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
      try {
        return await window.navigator.locks.request(CART_LOCK, { signal: controller.signal }, async () => {
          clearTimeout(timer);
          return operation();
        });
      } catch (error) {
        if (controller.signal.aborted) throw new Error('The cart is being updated in another window. Please try again shortly.');
        throw error;
      } finally { clearTimeout(timer); }
    }

    syncLegacyAccessories(cart) {
      if (!cart?.items) return;
      for (const card of document.querySelectorAll('[data-myart-legacy-accessory]')) {
        const count = cart.items.reduce((sum, item) => sum + (String(item.product_id) === card.dataset.productId ? Number(item.quantity) : 0), 0);
        const wrapper = card.querySelector('.together-quantity-wrapper');
        const quantity = card.querySelector('.together-qty');
        const add = card.querySelector('.productcard__item__together__item-add');
        const restore = count === 0 && wrapper?.contains(document.activeElement);
        wrapper?.classList.toggle('show', count > 0);
        if (quantity) quantity.textContent = String(count);
        if (add) add.style.display = count > 0 ? 'none' : '';
        if (restore && add?.isConnected) add.focus({ preventScroll: true });
      }
    }

    changeLegacyAccessory(card, delta, trigger) {
      if (!card?.dataset.productId || ![-1, 1].includes(delta)) return Promise.resolve({ ok: false });
      return this.enqueueMutation({
        label: 'Accessory quantity updated.',
        operation: async () => {
          const cart = await this.getCart();
          const matches = cart.items.filter((item) => String(item.product_id) === card.dataset.productId);
          if (!matches.length) return;
          const identities = new Set(matches.map((item) => this.lineIdentity(item)));
          if (identities.size !== 1) {
            this.open(trigger);
            throw new Error('This accessory has several options in your cart. Change the quantity beside the option you want.');
          }
          // Fresh keys only. Discount-split rows retain their complete identity.
          const item = matches.reduce((largest, line) => line.quantity > largest.quantity ? line : largest);
          const minimum = Number(item.quantity_rule?.min || 1);
          const quantity = Number(item.quantity) + delta * Number(item.quantity_rule?.increment || 1);
          await this.changeItem(cart, item, quantity < minimum ? 0 : quantity);
        },
      }).then((result) => {
        if (!result.ok && card.isConnected) {
          let status = card.querySelector('[data-legacy-accessory-status]');
          if (!status) {
            status = document.createElement('p');
            status.setAttribute('data-legacy-accessory-status', '');
            status.setAttribute('role', 'status');
            card.append(status);
          }
          status.textContent = result.error?.message || 'The cart could not be updated. Please try again.';
        } else card.querySelector('[data-legacy-accessory-status]')?.remove();
        return result;
      });
    }

    notifyCart(cart, { broadcast = true } = {}) {
      this.syncLegacyAccessories(cart);
      // Read-only reconciliation still refreshes other UI on this page. Only
      // owned writes notify other tabs, preventing a reconciliation echo loop.
      if (broadcast) this.channel?.postMessage({ changed: true });
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'myart-cart-drawer', cartData: cart });
      }
    }

    requestReconciliation({ review = false, historyRestore = false } = {}) {
      if (this.reconciliation && !review) return this.reconciliation;
      const nativeCart = document.querySelector('cart-items');
      const cartPath = new URL(this.dataset.cartUrl || '/cart', window.location.origin).pathname.replace(/\/$/, '');
      const nativePageVisible = typeof nativeCart?.refreshSharedCartPage === 'function' && !nativeCart.closest('cart-drawer') &&
        window.location.pathname.replace(/\/$/, '') === cartPath;
      if (!this.dialog?.open && !review && !historyRestore && !nativePageVisible && !document.querySelector('[data-myart-legacy-accessory]')) return Promise.resolve({ ok: true });
      this.reconciliation = this.enqueueMutation({
        operation: () => this.getCart(), allowReview: review,
        label: review ? 'Cart checked. Review the items before continuing.' : 'Cart updated.',
      }).finally(() => { this.reconciliation = null; });
      return this.reconciliation;
    }

    async checkout(control) {
      if (this.checkoutPending) return;
      this.pendingNote?.flush();
      this.checkoutPending = true;
      const destination = new URL(control.getAttribute('href') || `${this.dataset.rootUrl || '/'}checkout`, window.location.origin);
      try {
        const result = await this.enqueueMutation({ operation: async () => {}, label: 'Cart checked.', checkout: true });
        if (!result.ok || this.needsReview || !result.cart?.items?.length) return;
        if (destination.origin !== window.location.origin || !/\/checkout\/?$/.test(destination.pathname)) return;
        window.location.assign(destination.href);
      } finally { this.checkoutPending = false; }
    }

    setBusy(busy) {
      this.classList.toggle('is-busy', busy);
      this.toggleAttribute('aria-busy', busy);
      this.querySelectorAll('[data-cart-action], [data-cart-protection], [data-cart-quantity]').forEach((control) => {
        if (!(control instanceof HTMLButtonElement || control instanceof HTMLInputElement)) return;
        if (['close', 'close-sheet'].includes(control.dataset.cartAction)) return;
        if (!this.checkoutPending && ['increase', 'decrease', 'increase-card', 'decrease-card', 'remove'].includes(control.dataset.cartAction)) return;
        if (busy) {
          if (!Object.prototype.hasOwnProperty.call(control.dataset, 'cartWasDisabled')) control.dataset.cartWasDisabled = String(control.disabled);
          control.disabled = true;
        } else if (Object.prototype.hasOwnProperty.call(control.dataset, 'cartWasDisabled')) {
          control.disabled = control.dataset.cartWasDisabled === 'true';
          delete control.dataset.cartWasDisabled;
        }
      });
      this.querySelectorAll('[data-cart-checkout]').forEach((control) => {
        control.setAttribute('aria-disabled', String(busy || this.needsReview));
        control.setAttribute('aria-busy', String(busy));
      });
      this.syncNativeCheckoutGuard(busy || this.needsReview);
      this.renderControlFeedback();
    }

    syncNativeCheckoutGuard(blocked) {
      for (const control of document.querySelectorAll('#checkout[form="cart"], #cart [name="checkout"]')) {
        if (!(control instanceof HTMLButtonElement || control instanceof HTMLInputElement)) continue;
        if (blocked) {
          if (!Object.prototype.hasOwnProperty.call(control.dataset, 'myartWasDisabled')) control.dataset.myartWasDisabled = String(control.disabled);
          control.disabled = true;
          control.setAttribute('aria-busy', 'true');
        } else if (Object.prototype.hasOwnProperty.call(control.dataset, 'myartWasDisabled')) {
          control.disabled = control.dataset.myartWasDisabled === 'true';
          delete control.dataset.myartWasDisabled;
          control.removeAttribute('aria-busy');
        }
      }
      for (const group of document.querySelectorAll('.cart__dynamic-checkout-buttons')) {
        if (blocked) {
          if (!Object.prototype.hasOwnProperty.call(group.dataset, 'myartWasInert')) group.dataset.myartWasInert = String(group.hasAttribute('inert'));
          group.setAttribute('inert', '');
          group.setAttribute('aria-busy', 'true');
        } else if (Object.prototype.hasOwnProperty.call(group.dataset, 'myartWasInert')) {
          group.toggleAttribute('inert', group.dataset.myartWasInert === 'true');
          delete group.dataset.myartWasInert;
          group.removeAttribute('aria-busy');
        }
      }
    }

    // Tokens follow the user's intent, independently of replaced DOM nodes or
    // Shopify line-key changes. Several queued taps keep one spinner alive.
    withControlFeedback(control, operation) {
      const token = {
        control, action: control.dataset.cartAction || '', key: control.dataset.cartKey || '',
        handle: control.dataset.productHandle || '',
        protection: control.hasAttribute('data-cart-protection'),
        quantity: control.hasAttribute('data-cart-quantity'),
        gift: control.closest('[data-cart-line]')?.classList.contains('myart-cart__line--gift'),
      };
      this.controlFeedback.add(token);
      this.renderControlFeedback();
      let result;
      try { result = operation(); } catch (error) { result = Promise.reject(error); }
      const clearFeedback = () => {
        this.controlFeedback.delete(token);
        this.renderControlFeedback();
      };
      void Promise.resolve(this.whenMerchandiseConfirmed(result)).then(clearFeedback, clearFeedback);
      const tracked = Promise.resolve(result).catch((error) => {
        this.handleOperationError(error);
        return { ok: false, error };
      }).finally(clearFeedback);
      tracked.merchandiseConfirmed = Promise.resolve(this.whenMerchandiseConfirmed(result));
      return tracked;
    }

    renderControlFeedback() {
      const next = new Set();
      const controls = [...this.querySelectorAll('[data-cart-action], [data-cart-protection], [data-cart-quantity]')];
      for (const token of this.controlFeedback) {
        const identity = token.key && this.lineIdentities.get(token.key);
        const kindMatches = controls.filter((control) =>
          (token.protection ? control.hasAttribute('data-cart-protection')
            : token.quantity ? control.hasAttribute('data-cart-quantity') : control.dataset.cartAction === token.action) &&
          token.gift === control.closest('[data-cart-line]')?.classList.contains('myart-cart__line--gift') &&
          (!token.handle || control.dataset.productHandle === token.handle));
        const exact = kindMatches.filter((control) => control.dataset.cartKey === token.key);
        const replacements = !exact.length && identity ? kindMatches.filter((control) =>
          this.lineIdentities.get(control.dataset.cartKey) === identity) : [];
        for (const control of controls) {
          const sameKind = token.protection ? control.hasAttribute('data-cart-protection')
            : token.quantity ? control.hasAttribute('data-cart-quantity')
              : control.dataset.cartAction === token.action;
          const sameKey = !token.key || control.dataset.cartKey === token.key ||
            (replacements.length === 1 && control === replacements[0]);
          const sameGift = token.gift === control.closest('[data-cart-line]')?.classList.contains('myart-cart__line--gift');
          if (control !== token.control && !(sameKind && sameKey && sameGift &&
              (!token.handle || control.dataset.productHandle === token.handle))) continue;
          next.add(control);
          // Inputs retain their value; the spinner sits in their visible wrapper.
          if (token.protection) { const label = control.closest('.myart-cart__switch'); if (label) next.add(label); }
          if (token.quantity) { const group = control.closest('.myart-cart__quantity'); if (group) next.add(group); }
        }
        if (token.control.isConnected) next.add(token.control);
      }
      for (const node of this.feedbackNodes) {
        if (!next.has(node)) { node.removeAttribute('data-cart-pending'); node.removeAttribute('aria-busy'); }
      }
      for (const node of next) { node.setAttribute('data-cart-pending', ''); node.setAttribute('aria-busy', 'true'); }
      this.feedbackNodes = next;
    }

    cancelPromotionCelebration() {
      for (const animation of this.confettiAnimations) animation.cancel();
      this.confettiAnimations.clear();
      this.confettiLayer?.remove();
      this.confettiLayer = null;
    }

    flushPromotionCelebration() {
      if (!this.pendingPromotionCelebration || !this.dialog?.open) return;
      const earned = this.pendingPromotionCelebration;
      this.pendingPromotionCelebration = 0;
      if (!this.needsReview && Number(this.dataset.cartFreeKitCount || 0) >= earned) this.celebratePromotion();
    }

    celebratePromotion() {
      if (!this.dialog?.open || this.motionPreference?.matches || typeof this.dialog.animate !== 'function') return;
      this.cancelPromotionCelebration();
      const layer = document.createElement('div');
      layer.className = 'myart-cart__confetti';
      layer.setAttribute('aria-hidden', 'true');
      this.confettiLayer = layer;
      this.dialog.appendChild(layer);
      const width = this.dialog.clientWidth;
      const height = this.dialog.clientHeight;
      const colors = ['#ffd700', '#ffaa00', '#ff6b6b', '#4ecdc4', '#45b7d1'];
      try {
        for (let i = 0; i < 64; i += 1) {
          const piece = document.createElement('i');
          piece.style.backgroundColor = colors[i % colors.length];
          layer.appendChild(piece);
          const x = (Math.random() - 0.5) * width * 1.4;
          const y = -(0.18 + Math.random() * 0.35) * height;
          const rotation = Math.random() * 720 - 360;
          const animation = piece.animate([
            { transform: 'translate(0, 0) rotate(0deg)', opacity: 0 },
            { transform: `translate(${x * 0.7}px, ${y}px) rotate(${rotation * 0.4}deg)`, opacity: 1, offset: 0.35 },
            { transform: `translate(${x}px, ${height * 0.5}px) rotate(${rotation}deg)`, opacity: 0 },
          ], { duration: 1500 + Math.random() * 400, delay: i < 40 ? 0 : 220, easing: 'cubic-bezier(.2,.7,.4,1)' });
          this.confettiAnimations.add(animation);
          animation.addEventListener('finish', () => {
            this.confettiAnimations.delete(animation);
            animation.cancel();
            piece.remove();
            if (this.confettiLayer === layer && !this.confettiAnimations.size) { layer.remove(); this.confettiLayer = null; }
          }, { once: true });
        }
      } catch { this.cancelPromotionCelebration(); } // Decoration must never interrupt checkout.
    }

    async requestJson(url, options = {}) {
      if (!url) throw new Error('A Shopify cart route is missing.');
      if (!this.isConnected) throw new DOMException('Cart is no longer on this page.', 'AbortError');
      const requested = new URL(url, window.location.origin);
      const cartPath = new URL(this.dataset.cartUrl || '/cart', window.location.origin).pathname.replace(/\/$/, '');
      const path = requested.pathname.replace(/\.js$/, '').replace(/\/$/, '');
      const cartRequest = requested.origin === window.location.origin &&
        [cartPath, `${cartPath}/add`, `${cartPath}/change`, `${cartPath}/update`].includes(path);
      if (cartRequest && Date.now() < this.cartRetryAt) {
        const error = new Error('The store is busy. Please wait a moment, then select "Check cart again".');
        error.status = 429;
        error.rejected = true;
        error.retryAfterMs = this.cartRetryAt - Date.now();
        throw error;
      }
      const controller = new AbortController();
      this.requests.add(controller);
      const abort = () => controller.abort();
      options.signal?.addEventListener('abort', abort, { once: true });
      if (options.signal?.aborted) controller.abort();
      let expired = false;
      let timer;
      const deadline = new Promise((_, reject) => {
        timer = setTimeout(() => {
          expired = true;
          controller.abort();
          reject(new Error('The connection is taking too long. Check your connection and review the cart.'));
        }, REQUEST_TIMEOUT);
      });
      try {
        return await Promise.race([deadline, (async () => {
          let response;
          try {
            response = await fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal });
          } catch (cause) {
            if (cause?.name === 'AbortError') throw cause;
            const error = new Error('We could not connect to the store. Check your internet connection, then select "Check cart again".');
            error.category = 'network';
            error.cause = cause;
            throw error;
          }
          let data;
          if (response.status === 429) {
            const error = new Error('The store is busy. Please wait a moment, then select "Check cart again".');
            error.status = 429;
            error.rejected = true;
            const retryAfter = response.headers?.get?.('Retry-After');
            const seconds = Number(retryAfter);
            const requestedDelay = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : Date.parse(retryAfter) - Date.now();
            error.retryAfterMs = Number.isFinite(requestedDelay) && requestedDelay > 0 ? requestedDelay : 5000;
            // Honour Shopify's pause for reads, writes and render fallbacks.
            // Never schedule a POST retry; a later user action starts afresh.
            if (cartRequest) this.cartRetryAt = Math.max(this.cartRetryAt, Date.now() + error.retryAfterMs);
            throw error;
          }
          try { data = await response.json(); }
          catch (cause) {
            if (cause?.name === 'AbortError') throw cause;
            const error = new Error('The cart response could not be confirmed.');
            error.category = 'invalid-json';
            error.status = response.status;
            throw error;
          }
          if (!response.ok || data?.status || data?.errors) {
            const error = new Error(typeof data?.description === 'string' ? data.description : 'This change could not be saved. Review the available quantity and try again.');
            error.rejected = response.status >= 400 && response.status < 500;
            error.status = response.status;
            // Standard recipient forms need Shopify's structured field errors.
            error.errors = data?.errors || data?.description || error.message;
            throw error;
          }
          if (!data || typeof data !== 'object') throw new Error('The cart response could not be confirmed.');
          return data;
        })()]);
      } catch (error) {
        if (options.method === 'POST' && !error.rejected) error.uncertain = true;
        if (expired) error.name = 'CartTimeoutError';
        throw error;
      } finally {
        clearTimeout(timer);
        options.signal?.removeEventListener('abort', abort);
        this.requests.delete(controller);
      }
    }

    postJson(url, body) {
      const form = typeof FormData === 'function' && body instanceof FormData;
      this.bundledSections = null;
      this.writeRevision += 1;
      return this.requestJson(url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...(form ? {} : { 'Content-Type': 'application/json' }) },
        body: form ? body : JSON.stringify(body),
      });
    }

    readBundledConfirmation(sections, writeRevision) {
      // Only consume the section delivered with this write. Never reuse the
      // page's initial state, an earlier response or a recommendation response.
      if (writeRevision !== this.writeRevision || !this.dataset.cartConfirmationTheme) return null;
      const html = sections?.['cart-drawer'];
      if (typeof html !== 'string' || !html) return null;
      try {
        const page = new DOMParser().parseFromString(html, 'text/html');
        const hosts = page.querySelectorAll('cart-drawer.myart-cart-host');
        if (hosts.length !== 1) return null;
        const host = hosts[0];
        const state = JSON.parse(host.getAttribute('data-cart-confirmation') || 'null');
        if (state?.version !== 1 || state.theme !== this.dataset.cartConfirmationTheme ||
            state.root !== this.dataset.rootUrl || host.dataset.cartConfirmationTheme !== state.theme) return null;
        const cart = state.cart;
        if (!this.isCompleteCart(cart) || cart.currency !== this.dataset.currency ||
            JSON.stringify(this.cartState(cart)) !== JSON.stringify(JSON.parse(host.dataset.cartState || 'null'))) return null;
        const amount = (value) => Number.isSafeInteger(value) && value >= 0;
        if (!amount(cart.items_subtotal_price) || !amount(cart.original_total_price) ||
            cart.note !== '' || !cart.attributes || Object.keys(cart.attributes).length) return null;
        for (const item of cart.items) {
          if (item.id !== item.variant_id || !Number.isSafeInteger(item.product_id) || item.product_id <= 0 ||
              !item.properties || Object.keys(item.properties).length || item.gift_card !== false ||
              item.selling_plan_allocation || item.parent_relationship || item.item_components?.length ||
              !['handle', 'title', 'product_title'].every((key) => typeof item[key] === 'string') || !item.handle ||
              !['price', 'original_price', 'final_price', 'total_discount'].every((key) => amount(item[key])) ||
              !Array.isArray(item.line_level_discount_allocations) ||
              !item.line_level_discount_allocations.every((allocation) => amount(allocation.amount)) ||
              item.instructions?.can_remove !== true || item.instructions?.can_update_quantity !== true) return null;
          const rule = item.quantity_rule;
          if (!rule || !Number.isSafeInteger(rule.min) || rule.min < 1 ||
              !Number.isSafeInteger(rule.increment) || rule.increment < 1 ||
              !(rule.max === null || (Number.isSafeInteger(rule.max) && rule.max >= rule.min))) return null;
        }
        return cart;
      } catch { return null; } // Missing/unsupported projection keeps the established GET path.
    }

    async confirmedWrite(url, body, matches, { renderSections = true } = {}) {
      const context = new URL(this.withThemeContext(this.dataset.cartUrl || '/cart'));
      const sectionOptions = renderSections ? { sections: [...SECTION_IDS], sections_url: context.pathname + context.search } : {};
      const form = typeof FormData === 'function' && body instanceof FormData;
      let payload;
      if (form) {
        payload = new FormData();
        for (const [key, value] of body.entries()) payload.append(key, value);
        if (renderSections) {
          payload.set('sections', SECTION_IDS.join(','));
          payload.set('sections_url', sectionOptions.sections_url);
        } else {
          payload.delete('sections');
          payload.delete('sections_url');
        }
      } else payload = { ...body, ...sectionOptions };
      let submitted = false;
      const writeRevision = this.writeRevision + 1;
      try {
        const response = await this.postJson(url, payload);
        submitted = true;
        // Add's items are not a complete cart. A supported server-rendered
        // projection can confirm the full cart without another round trip.
        const fullCart = /\/(?:change|update)(?:\.js)?$/.test(new URL(url, window.location.origin).pathname) && this.isCompleteCart(response);
        const projected = !fullCart && /\/add(?:\.js)?$/.test(new URL(url, window.location.origin).pathname)
          ? this.readBundledConfirmation(response.sections, writeRevision) : null;
        const confirmedProjection = projected && matches(projected, response);
        const actual = fullCart ? this.acceptCart(response, writeRevision)
          : confirmedProjection ? this.acceptCart(projected, writeRevision) : await this.getCart();
        if (!matches(actual, response)) {
          const error = new Error('The saved cart differs from the requested change. Review the updated items.');
          error.uncertain = true;
          throw error;
        }
        this.confirmedWriteRevision = Math.max(this.confirmedWriteRevision, writeRevision);
        this.rememberBundledSections(response.sections, actual, writeRevision);
        return response;
      }
      catch (error) {
        // Shopify can return HTTP400 for section parameters AFTER saving a cart.
        // Never interpret that response as permission to repeat the POST.
        if (submitted || error.status === 400) error.uncertain = true;
        if (!error.uncertain) throw error;
        try {
          const actual = await this.getCart();
          if (matches(actual)) {
            this.confirmedWriteRevision = Math.max(this.confirmedWriteRevision, writeRevision);
            return actual;
          }
        } catch { /* The unknown write is never repeated automatically. */ }
        this.requireCartReview();
        const uncertain = new Error('We could not confirm the last change. It may already be saved. Select Check cart and review the items before trying again.');
        uncertain.uncertain = true;
        throw uncertain;
      }
    }

    async getCart() {
      // A queued write must always take its own fresh baseline/confirmation.
      if (this.pendingMutations) return this.readCurrentCart();
      if (this.cartReadRequest?.revision === this.writeRevision) return this.cartReadRequest.promise;
      const request = { revision: this.writeRevision };
      request.promise = this.readCurrentCart();
      this.cartReadRequest = request;
      try { return await request.promise; }
      finally { if (this.cartReadRequest === request) this.cartReadRequest = null; }
    }

    async readCurrentCart() {
      this.bundledSections = null;
      const writeRevision = this.writeRevision;
      const route = new URL(this.dataset.cartUrl || '/cart', window.location.origin);
      route.pathname = `${route.pathname.replace(/\/$/, '')}.js`;
      const cart = await this.requestJson(route.href, {
        cache: 'no-store',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!this.isCompleteCart(cart)) throw new Error('The latest cart could not be read.');
      return this.acceptCart(cart, writeRevision);
    }

    isCompleteCart(cart) {
      const amount = (value) => Number.isSafeInteger(value) && value >= 0;
      if (!cart || !Array.isArray(cart.items) || !amount(cart.item_count) ||
          !amount(cart.total_price) || !amount(cart.total_discount) || !/^[A-Z]{3}$/.test(cart.currency || '')) return false;
      const keys = new Set();
      let count = 0;
      for (const item of cart.items) {
        if (!item || typeof item.key !== 'string' || !item.key || keys.has(item.key) ||
            !Number.isSafeInteger(item.variant_id) || item.variant_id <= 0 ||
            !Number.isSafeInteger(item.quantity) || item.quantity <= 0 ||
            !amount(item.final_line_price) || !amount(item.original_line_price)) return false;
        keys.add(item.key);
        count += item.quantity;
      }
      return count === cart.item_count;
    }

    acceptCart(cart, writeRevision) {
      for (const item of cart.items) {
        this.lineIdentities.set(item.key, this.lineIdentity(item));
        this.lineSnapshots.set(item.key, item);
      }
      this.lastCart = cart;
      this.cartReadVersion += 1;
      this.cartReadWriteRevision = writeRevision;
      return cart;
    }

    cartState(cart) {
      return [cart.currency, cart.item_count, cart.total_price, cart.total_discount,
        cart.items.map((item) => [item.key, item.quantity, item.original_line_price, item.final_line_price])];
    }

    rememberBundledSections(sections, cart, writeRevision) {
      if (writeRevision !== this.writeRevision || this.cartReadWriteRevision !== writeRevision ||
          !SECTION_IDS.every((id) => typeof sections?.[id] === 'string' && sections[id].trim())) return;
      try {
        const drawer = parseDocument(sections['cart-drawer']).querySelector('cart-drawer');
        // The HTML may precede the verifying GET after add. Reuse it only when
        // its ordered keys, quantities, prices and currency still match Shopify.
        if (!drawer?.classList.contains('myart-cart-host') || !this.matchesPreviewTheme(drawer) ||
            JSON.stringify(JSON.parse(drawer.dataset.cartState)) !== JSON.stringify(this.cartState(cart))) return;
        this.bundledSections = { sections, writeRevision, readVersion: this.cartReadVersion };
      } catch { /* Null, old-theme or malformed HTML falls back to a fresh GET. */ }
    }

    async synchronizeProtection(mode, verifiedCart = null, { fresh = false } = {}) {
      const handle = String(this.dataset.protectionHandle || '').trim();
      let cart = verifiedCart || await this.getCart();
      if (!handle) return cart;
      let protectionItems = cart.items.filter((item) => item.handle === handle);
      const coreItems = cart.items.filter((item) => item.handle !== handle);
      const baseTotal = coreItems.reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
      const desired = mode === 'auto' ? (this.protectionIntent ?? protectionItems.length > 0) : Boolean(mode);
      this.protectionIntent = desired;
      let variant = null;
      if (desired && coreItems.length && baseTotal > 0) {
        const product = await this.fetchProduct(handle, { fresh, priority: true, maxAge: PROTECTION_CACHE_TTL });
        const target = Math.floor(baseTotal * 10 / 100);
        variant = (product.variants || []).filter((item) => item.available !== false).reduce((closest, item) =>
          !closest || Math.abs(Number(item.price) - target) < Math.abs(Number(closest.price) - target) ? item : closest, null);
      }
      const kept = variant && protectionItems.find((item) => Number(item.variant_id) === Number(variant.id));
      const updates = {};
      for (const item of protectionItems) {
        const quantity = kept && item.key === kept.key ? 1 : 0;
        if (item.quantity !== quantity) updates[item.key] = quantity;
      }
      // Remove old charge before adding a replacement: a partial outage can
      // leave protection absent, but cannot introduce two paid prices.
      if (Object.keys(updates).length) {
        const matches = this.quantityContract(cart, updates);
        await this.confirmedWrite(this.dataset.cartUpdateUrl, { updates }, (actual) => {
          const lines = actual.items.filter((item) => item.handle === handle);
          return matches(actual) && (kept ? lines.length === 1 && lines[0].variant_id === kept.variant_id && lines[0].quantity === 1 : lines.length === 0);
        }, { renderSections: !variant || Boolean(kept) });
        cart = this.cartReadWriteRevision === this.writeRevision ? this.lastCart : await this.getCart();
        protectionItems = cart.items.filter((item) => item.handle === handle);
      }
      if (variant && !protectionItems.length) {
        const matches = this.quantityContract(cart, {}, [{ variant_id: Number(variant.id), quantity: 1, properties: {} }]);
        await this.confirmedWrite(this.dataset.cartAddUrl, { items: [{ id: Number(variant.id), quantity: 1 }] }, (actual) => {
          const lines = actual.items.filter((item) => item.handle === handle);
          return matches(actual) && lines.length === 1 && Number(lines[0].variant_id) === Number(variant.id) && lines[0].quantity === 1;
        });
      }
      // confirmedWrite already read Shopify and checked the saved result.
      // Do not repeat that same read without another intervening write.
      if (this.cartReadWriteRevision === this.writeRevision && this.lastCart) cart = this.lastCart;
      else cart = await this.getCart();
      const finalItems = cart.items.filter((item) => item.handle === handle);
      const valid = variant ? finalItems.length === 1 && Number(finalItems[0].variant_id) === Number(variant.id) && finalItems[0].quantity === 1 : finalItems.length === 0;
      if (!valid) throw new Error('Shipping Protection could not be confirmed. Select Check cart before checkout.');
      if (desired && coreItems.length && baseTotal > 0 && !variant) {
        this.pendingNotice = 'Shipping Protection is unavailable and has been removed. Your products are still in the cart.';
      }
      this.protectionIntent = null;
      return cart;
    }

    fetchProduct(handle, { fresh = false, priority = false, maxAge = PRODUCT_CACHE_TTL } = {}) {
      const cleanHandle = String(handle || '').trim();
      if (!cleanHandle) return Promise.reject(new Error('Product handle is missing.'));
      const cacheKey = `${this.dataset.rootUrl}|${this.dataset.currency}|${cleanHandle}`;
      const cached = this.productCache.get(cacheKey);
      if (!fresh && cached && Date.now() - cached.time < maxAge) return cached.request;
      const root = String(this.dataset.rootUrl || '/').replace(/\/?$/, '/');
      const request = this.queueProductRead(() => this.requestJson(`${root}products/${encodeURIComponent(cleanHandle)}.js`, {
        cache: fresh || priority ? 'no-store' : 'default',
        headers: { Accept: 'application/json' },
      }), fresh || priority).catch((error) => {
        if (this.productCache.get(cacheKey)?.request === request) this.productCache.delete(cacheKey);
        throw error;
      });
      this.productCache.set(cacheKey, { time: Date.now(), request });
      return request;
    }

    queueProductRead(operation, priority = false) {
      return new Promise((resolve, reject) => {
        const job = { operation, resolve, reject };
        if (priority) this.productReadQueue.unshift(job);
        else this.productReadQueue.push(job);
        this.drainProductReads();
      });
    }

    drainProductReads() {
      while (this.activeProductReads < 4 && this.productReadQueue.length) {
        const job = this.productReadQueue.shift();
        this.activeProductReads += 1;
        Promise.resolve().then(() => {
          if (Date.now() < this.productRetryAt) {
            const error = new Error('The store is busy. Please wait a moment, then select "Check cart again".');
            error.status = 429;
            throw error;
          }
          return job.operation();
        }).catch((error) => {
          if (error.status === 429) this.productRetryAt = Math.max(this.productRetryAt, Date.now() + (error.retryAfterMs || 5000));
          throw error;
        }).then(job.resolve, job.reject).finally(() => {
          this.activeProductReads -= 1;
          this.drainProductReads();
        });
      }
    }

    // Reuse the verified option form and writer without opening the cart view.
    async openAccessoryOptions(trigger) {
      return this.openProductSheet(String(trigger?.dataset.productHandle || ''), {}, trigger, { standalone: true });
    }

    showAccessoryOptions(trigger) {
      const sheet = this.querySelector('[data-cart-sheet="product"]');
      if (!sheet || this.dialog?.open) throw new Error('Close the cart before choosing this accessory.');
      const popup = document.createElement('dialog');
      popup.className = 'myart-cart myart-cart--accessory-options';
      if (trigger?.matches('[data-wishlist-cart-add]')) popup.classList.add('myart-cart--wishlist-options');
      popup.setAttribute('data-myart-modal-motion', '');
      popup.setAttribute('aria-labelledby', 'MyArtCartProductSheetTitle');
      this.accessorySheetSemantics = { role: sheet.getAttribute('role'), modal: sheet.getAttribute('aria-modal') };
      sheet.removeAttribute('role');
      sheet.removeAttribute('aria-modal');
      this.accessorySheetParent = sheet.parentNode;
      this.accessoryDialog = popup;
      popup.appendChild(sheet);
      this.appendChild(popup);
      popup.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || this.accessoryDialog !== popup || !popup.open) return;
        this.lastInputWasKeyboard = true;
        event.preventDefault();
        event.stopPropagation();
        this.closeSheet();
      });
      popup.addEventListener('cancel', (event) => { event.preventDefault(); event.stopPropagation(); this.closeSheet(); });
      popup.addEventListener('click', (event) => {
        if (event.target === popup || event.target === sheet) this.closeSheet();
      });
      popup.addEventListener('close', () => {
        if (this.accessoryDialog === popup) this.closeSheet();
      });
      const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      const pagePadding = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.setProperty('--myart-cart-page-padding', `${pagePadding + scrollbarWidth}px`);
      document.body.classList.add('myart-accessory-options-open');
      popup.myartNativeReturnTarget = document.activeElement;
      popup.showModal();
      this.openSheet('product', trigger);
    }

    async openProductSheet(handle, properties, trigger, { standalone = false } = {}) {
      if (!handle) {
        this.showError('This product is unavailable.');
        return;
      }
      this.closeSheet({ restoreFocus: false });
      const version = ++this.productRequestVersion;
      trigger?.setAttribute('aria-busy', 'true');
      if (trigger instanceof HTMLButtonElement) trigger.disabled = true;
      try {
        const product = await this.fetchProduct(handle, { priority: standalone });
        if (version !== this.productRequestVersion || !this.isConnected || (!standalone && !this.dialog?.open)) return;
        if (standalone && !trigger?.isConnected) return;
        this.currentProductState = { product, properties, trigger };
        this.populateProductSheet(product);
        if (standalone) this.showAccessoryOptions(trigger);
        else this.openSheet('product', trigger);
        return { ok: true, options: true };
      } catch (error) {
        if (standalone) {
          if (version === this.productRequestVersion) this.closeSheet();
          if (trigger?.matches('[data-wishlist-cart-add]')) {
            this.open(trigger, { refresh: false });
            this.handleOperationError(error);
          }
          return { ok: false, error };
        }
        if (version === this.productRequestVersion) this.handleOperationError(error);
      } finally {
        trigger?.removeAttribute('aria-busy');
        if (trigger instanceof HTMLButtonElement) trigger.disabled = false;
      }
    }

    populateProductSheet(product) {
      const sheet = this.querySelector('[data-cart-sheet="product"]');
      if (!sheet) return;
      const isBundle = Boolean(this.currentProductState?.properties?._bundle_id);
      sheet.dataset.sheetKind = isBundle ? 'bundle' : 'product';
      const availableVariant = (product.variants || []).find((variant) => variant.available !== false) || product.variants?.[0];
      if (!availableVariant) throw new Error('This product has no available variant.');

      const title = sheet.querySelector('[data-product-sheet-title]');
      const image = sheet.querySelector('[data-product-sheet-image]');
      const optionsContainer = sheet.querySelector('[data-product-sheet-options]');
      title.textContent = String(product.title || 'Product');
      title.href = safeUrl(product.url, `${this.dataset.rootUrl || '/'}products/${encodeURIComponent(product.handle || '')}`);
      setImageSource(image, availableVariant.featured_image?.src || product.featured_image || product.images?.[0]);
      image.alt = String(product.title || '');
      optionsContainer.replaceChildren();
      this.sheetSequence += 1;

      (product.options || []).forEach((option, optionIndex) => {
        const values = option.values || [];
        const name = String(option.name || `Option ${optionIndex + 1}`);
        const normalizedName = name.toLowerCase();
        if (normalizedName === 'title' && values.length === 1 && String(values[0]).toLowerCase() === 'default title') return;
        const kind = normalizedName.includes('format') ? 'format'
          : normalizedName.includes('color') || normalizedName.includes('difficulty') ? 'color'
            : normalizedName.includes('size') ? 'size' : 'other';
        const fieldset = document.createElement('fieldset');
        const legend = document.createElement('legend');
        const valueList = document.createElement('div');
        fieldset.className = 'myart-cart__option-group';
        fieldset.dataset.optionKind = kind;
        legend.textContent = isBundle ? `Select The ${name}:` : `${name}:`;
        valueList.className = 'myart-cart__option-values';
        values.forEach((rawValue, valueIndex) => {
          const value = String(rawValue || '');
          const label = document.createElement('label');
          const input = document.createElement('input');
          const valueLabel = document.createElement('span');
          const text = document.createElement('span');
          input.type = 'radio';
          input.name = `myart-cart-option-${this.sheetSequence}-${optionIndex}`;
          input.value = value;
          input.dataset.optionIndex = String(optionIndex);
          input.id = `MyArtCartOption-${this.sheetSequence}-${optionIndex}-${valueIndex}`;
          input.checked = String(availableVariant.options?.[optionIndex] || '') === value;
          valueLabel.className = 'myart-cart__option-label';
          text.className = 'myart-cart__option-text';
          const parts = value.match(/^(.+?)\s*(\(.+\))?$/);
          text.textContent = parts?.[1]?.trim() || value;
          valueLabel.appendChild(text);
          if (parts?.[2]) {
            const extra = document.createElement('span');
            extra.className = 'myart-cart__option-extra';
            extra.textContent = /^36\s*colors/i.test(value) ? '(+5 hours)' : parts[2];
            valueLabel.appendChild(extra);
          }
          if (kind === 'format') {
            const normalizedValue = value.toLowerCase();
            if (normalizedValue.includes('rolled')) valueLabel.dataset.format = 'rolled';
            else if (normalizedValue.includes('stretched')) valueLabel.dataset.format = 'stretched';
          }
          label.htmlFor = input.id;
          label.append(input, valueLabel);
          valueList.appendChild(label);
        });
        fieldset.append(legend, valueList);
        optionsContainer.appendChild(fieldset);
      });
      this.updateProductSheetVariant();
    }

    selectedProductVariant() {
      const state = this.currentProductState;
      const sheet = this.querySelector('[data-cart-sheet="product"]');
      if (!state || !sheet) return null;
      const fallback = (state.product.variants || []).find((variant) => variant.available !== false) || state.product.variants?.[0];
      const selected = (state.product.options || []).map((option, index) => {
        return sheet.querySelector(`[data-option-index="${index}"]:checked`)?.value || fallback?.options?.[index];
      });
      return (state.product.variants || []).find((variant) => {
        return (variant.options || []).every((value, index) => String(value) === String(selected[index]));
      }) || null;
    }

    updateProductSheetOptionAvailability() {
      const state = this.currentProductState;
      const sheet = this.querySelector('[data-cart-sheet="product"]');
      if (!state || !sheet) return;
      const variants = state.product.variants || [];
      const fallback = variants.find((variant) => variant.available !== false) || variants[0];
      const selected = (state.product.options || []).map((option, index) => {
        return sheet.querySelector(`[data-option-index="${index}"]:checked`)?.value || fallback?.options?.[index];
      });
      const inputs = [...sheet.querySelectorAll('[data-option-index]')];
      (state.product.options || []).forEach((option, optionIndex) => {
        // Later selections must not hide every route to another existing
        // combination. Resolve each option against the already chosen prefix.
        const candidates = variants.filter((variant) => (variant.options || []).every((value, index) =>
          index >= optionIndex || String(value) === String(selected[index])));
        const controls = inputs.filter((input) => Number(input.dataset.optionIndex) === optionIndex);
        controls.forEach((input) => {
          const exists = candidates.some((variant) => String(variant.options?.[optionIndex]) === input.value);
          input.disabled = !exists;
          const label = input.closest('label');
          if (label) {
            label.hidden = !exists;
            label.toggleAttribute('aria-hidden', !exists);
          }
        });
        let checked = controls.find((input) => input.checked && !input.disabled);
        if (!checked && controls.length) {
          const replacement = candidates.find((variant) => variant.available !== false) || candidates[0];
          checked = controls.find((input) => !input.disabled && input.value === String(replacement?.options?.[optionIndex]));
          controls.forEach((input) => { input.checked = input === checked; });
        }
        if (checked) selected[optionIndex] = checked.value;
      });
    }

    updateProductSheetVariant() {
      const sheet = this.querySelector('[data-cart-sheet="product"]');
      if (!sheet) return;
      this.updateProductSheetOptionAvailability();
      const variant = this.selectedProductVariant();
      const state = this.currentProductState;
      const description = sheet.querySelector('[data-product-sheet-description]');
      const price = sheet.querySelector('[data-product-sheet-price]');
      const compare = sheet.querySelector('[data-product-sheet-compare]');
      const image = sheet.querySelector('[data-product-sheet-image]');
      const submit = sheet.querySelector('[data-cart-action="submit-product"]');
      const error = sheet.querySelector('[data-product-sheet-error]');
      if (error) { error.hidden = true; error.textContent = ''; }
      if (!variant) {
        description.textContent = 'This combination is unavailable.';
        price.textContent = '';
        compare.hidden = true;
        compare.textContent = '';
        submit.disabled = true;
        submit.textContent = 'Unavailable';
        return;
      }
      description.textContent = (variant.options || []).filter((value) => String(value).toLowerCase() !== 'default title').join(' | ');
      price.textContent = this.formatMoney(variant.price);
      const comparePrice = Number(variant.compare_at_price || 0);
      compare.hidden = !(comparePrice > Number(variant.price));
      compare.textContent = compare.hidden ? '' : this.formatMoney(comparePrice);
      setImageSource(image, variant.featured_image?.src || state?.product?.featured_image || state?.product?.images?.[0]);
      submit.disabled = variant.available === false;
      submit.textContent = variant.available === false ? 'Sold out' : 'Add Cart';
    }

    async submitProductSheet() {
      const submit = this.querySelector('[data-cart-action="submit-product"]');
      if (!submit || submit.disabled || submit.hasAttribute('data-cart-pending')) return;
      const state = this.currentProductState;
      const variant = this.selectedProductVariant();
      const error = this.querySelector('[data-product-sheet-error]');
      if (!variant || variant.available === false) {
        if (error) {
          error.hidden = false;
          error.textContent = 'Selected variant is unavailable.';
        }
        return;
      }
      const properties = this.currentProductState?.properties || {};
      const result = await this.whenMerchandiseConfirmed(this.withControlFeedback(submit, () => this.addVariant(variant.id, properties, 'Product added.')));
      if (result?.ok && this.currentProductState === state) {
        if (state.trigger?.matches('[data-wishlist-cart-add]')) {
          // Release the standalone selector before opening the confirmed cart.
          this.closeSheet({ restoreFocus: false, immediate: true });
          this.open(state.trigger, { refresh: false });
        } else this.closeSheet();
      }
      else if (!result?.ok && this.currentProductState === state && error) {
        error.hidden = false;
        error.textContent = result?.error?.message || 'The product could not be added. Review the cart and try again.';
      }
    }

    openPhotoPreview(trigger) {
      const url = safePhotoUrl(trigger?.getAttribute('href'));
      const sheet = this.querySelector('[data-cart-sheet="photo"]');
      if (!url || !sheet) return;
      this.openSheet('photo', trigger);
      this.clearPhotoPreview();
      this.photoController = new AbortController();
      const { signal } = this.photoController;
      const image = sheet.querySelector('[data-cart-photo-image]');
      const status = sheet.querySelector('[data-cart-photo-status]');
      const finish = (failed) => {
        clearTimeout(this.photoTimeout);
        if (signal.aborted || sheet.hidden) return;
        image.hidden = failed;
        status.hidden = !failed;
        status.textContent = failed ? 'The photo could not load. Close this window and try again.' : '';
        sheet.setAttribute('aria-busy', 'false');
        if (failed) { this.photoController.abort(); image.removeAttribute('src'); }
      };
      image.hidden = true;
      status.hidden = false;
      status.textContent = 'Loading your photo…';
      sheet.setAttribute('aria-busy', 'true');
      image.addEventListener('load', () => finish(false), { once: true, signal });
      image.addEventListener('error', () => finish(true), { once: true, signal });
      this.photoTimeout = setTimeout(() => finish(true), 15000);
      image.src = url;
    }

    clearPhotoPreview() {
      clearTimeout(this.photoTimeout);
      this.photoController?.abort();
      this.photoController = null;
      const sheet = this.querySelector('[data-cart-sheet="photo"]');
      if (!sheet) return;
      const image = sheet.querySelector('[data-cart-photo-image]');
      image?.removeAttribute('src');
      if (image) image.hidden = true;
      sheet.setAttribute('aria-busy', 'false');
    }

    openSheet(name, trigger) {
      const sheet = this.querySelector(`[data-cart-sheet="${name}"]`);
      if (!sheet || !this.view) return;
      if (this.getOpenSheet()) this.closeSheet({ restoreFocus: false });
      this.sheetTrigger = trigger instanceof HTMLElement ? trigger : document.activeElement;
      this.view.inert = true;
      window.MyArtModalMotion ? window.MyArtModalMotion.open(sheet, () => { sheet.hidden = false; }, sheet.querySelector('.myart-cart__sheet-panel') || sheet) : (sheet.hidden = false);
      queueMicrotask(() => {
        if (sheet.hidden || !sheet.isConnected || !(this.accessoryDialog?.open || this.dialog?.open)) return;
        const focusTarget = sheet.querySelector(FOCUSABLE_SELECTOR) || sheet;
        if (!sheet.hasAttribute('tabindex')) sheet.tabIndex = -1;
        focusTarget.focus({ preventScroll: true });
      });
    }

    getOpenSheet() {
      return this.querySelector('[data-cart-sheet]:not([hidden])');
    }

    closeSheet({ restoreFocus = true, immediate = !restoreFocus } = {}) {
      this.productRequestVersion += 1;
      const sheet = this.getOpenSheet();
      if (!sheet) { this.currentProductState = null; return; }
      if (!immediate && window.MyArtModalMotion && (this.dialog?.open || this.accessoryDialog?.open)) {
        this.accessoryDialog?.setAttribute('data-myart-modal-closing', '');
        window.MyArtModalMotion.close(sheet, () => this.finishCloseSheet(sheet, restoreFocus), sheet.querySelector('.myart-cart__sheet-panel') || sheet);
        return;
      }
      window.MyArtModalMotion?.cancel(sheet);
      this.finishCloseSheet(sheet, restoreFocus);
    }

    finishCloseSheet(sheet, restoreFocus) {
      sheet.hidden = true;
      if (sheet.dataset.cartSheet === 'photo') this.clearPhotoPreview();
      if (this.view) this.view.inert = false;
      const trigger = this.sheetTrigger;
      this.sheetTrigger = null;
      if (sheet.dataset.cartSheet === 'product') this.currentProductState = null;
      const closedAccessoryDialog = this.accessoryDialog;
      let nativeRestoredFocus = null;
      if (this.accessoryDialog) {
        const popup = this.accessoryDialog;
        this.accessoryDialog = null;
        this.accessorySheetParent?.appendChild(sheet);
        this.accessorySheetParent = null;
        if (this.accessorySheetSemantics?.role != null) sheet.setAttribute('role', this.accessorySheetSemantics.role);
        if (this.accessorySheetSemantics?.modal != null) sheet.setAttribute('aria-modal', this.accessorySheetSemantics.modal);
        this.accessorySheetSemantics = null;
        if (popup.open) popup.close();
        if (document.activeElement === popup.myartNativeReturnTarget) nativeRestoredFocus = document.activeElement;
        popup.remove();
        document.body.classList.remove('myart-accessory-options-open');
        document.body.style.removeProperty('--myart-cart-page-padding');
      }
      if (restoreFocus) queueMicrotask(() => {
        if (this.getOpenSheet()) return;
        const replacement = sheet.dataset.cartSheet === 'photo' && trigger?.getAttribute('href')
          ? [...this.querySelectorAll('[data-cart-action="view-photo"]')].find((link) => link.getAttribute('href') === trigger.getAttribute('href')) : null;
        const accessory = trigger?.closest('[data-pbn-main-accessories], [data-pbn-accessories]');
        const quantity = accessory && trigger.hidden ? trigger.closest('article')?.querySelector('.together-qty-btn.plus') : null;
        const returnTarget = quantity || (trigger?.isConnected ? trigger : replacement || this.querySelector('[data-cart-action="close"]'));
        if (closedAccessoryDialog) this.restoreClosedModalFocus(returnTarget, closedAccessoryDialog, sheet, nativeRestoredFocus);
        else returnTarget?.focus({ preventScroll: true });
      });
    }

    scheduleEnhancements() {
      const version = this.renderVersion;
      const run = () => {
        if (!this.isConnected || !this.dialog?.open || version !== this.renderVersion) return;
        this.loadRecommendations(version).catch((error) => console.warn('Cart recommendations unavailable.', error));
        this.renderBundles(version).catch((error) => console.warn('Cart bundles unavailable.', error));
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, { once: true, signal: this.eventController?.signal });
      } else {
        queueMicrotask(run);
      }
    }

    recordProductVisit() {
      const match = window.location.pathname.match(/\/products\/([^/]+)\/?$/);
      if (!match) return;
      try {
        const handle = decodeURIComponent(match[1]);
        if (!/^[a-zA-Z0-9_-]+$/.test(handle) || handle === this.dataset.protectionHandle) return;
        const history = this.readProductVisits().filter(item => item.handle !== handle);
        const pageOnly = Boolean(document.querySelector('pyp-photo-upload, pn-photo-upload, ai-pbn-personalizer'));
        localStorage.setItem('myart-recent-products-v1', JSON.stringify([{ handle, pageOnly }, ...history].slice(0, 20)));
      } catch { /* Storage is optional; server-rendered bestsellers remain available. */ }
    }

    readProductVisits() {
      try {
        const value = JSON.parse(localStorage.getItem('myart-recent-products-v1') || '[]');
        if (!Array.isArray(value)) return [];
        const seen = new Set();
        return value.map(item => typeof item === 'string' ? { handle: item, pageOnly: true } : item).filter(item => {
          if (!item || typeof item.handle !== 'string' || !/^[a-zA-Z0-9_-]+$/.test(item.handle) || seen.has(item.handle)) return false;
          seen.add(item.handle); return true;
        }).map(item => ({ handle: item.handle, pageOnly: item.pageOnly !== false })).slice(0, 20);
      } catch { return []; }
    }

    async rankedProductCards(handle) {
      const key = `ranked-kits:${handle}`;
      const cached = this.recommendationCache.get(key);
      if (!cached || Date.now() - cached.time >= PRODUCT_CACHE_TTL) {
        const request = (async () => {
          const cards = [], seen = new Set();
          let page = 1;
          // Forty ranked kits cover twenty places even when all twenty viewed
          // products also occur at the start of the sales ranking.
          while (page && cards.length < 40) {
            const url = new URL(`${this.dataset.rootUrl || '/'}collections/${handle}`, window.location.origin);
            url.searchParams.set('sort_by', 'best-selling');
            url.searchParams.set('sections', 'cart-drawer');
            url.searchParams.set('page', String(page));
            const sections = await this.requestJson(this.withThemeContext(url.toString()));
            const template = parseDocument(sections['cart-drawer'] || '').querySelector('template[data-cart-ranked-products]');
            if (!template) throw new Error('Ranked products unavailable.');
            for (const card of template.content.querySelectorAll('[data-cart-product-card]')) {
              const productHandle = card.dataset.productHandle;
              if (productHandle && !seen.has(productHandle)) { seen.add(productHandle); cards.push(card); }
            }
            const next = Number(template.dataset.nextPage);
            page = Number.isInteger(next) && next > page ? next : 0;
          }
          return cards;
        })().catch(error => { this.recommendationCache.delete(key); throw error; });
        this.recommendationCache.set(key, { time: Date.now(), request });
      }
      return (await this.recommendationCache.get(key).request).map(card => card.cloneNode(true));
    }

    syncRecommendationQuantities() {
      if (!Array.isArray(this.lastCart?.items)) return;
      this.querySelectorAll('[data-cart-bestsellers] .myart-cart__product-card').forEach(card => {
        const add = card.querySelector('[data-cart-action="add-product"]');
        if (!add) return;
        const lines = this.lastCart.items.filter(item => item.handle === card.dataset.productHandle);
        const line = lines.length === 1 ? lines[0] : null;
        let quantity = card.querySelector('.myart-cart__product-quantity');
        add.classList.toggle('is-hidden', Boolean(line));
        if (!line) { quantity?.remove(); return; }
        if (!quantity) {
          quantity = document.createElement('div');
          quantity.className = 'myart-cart__product-quantity';
          quantity.setAttribute('role', 'group');
          quantity.setAttribute('aria-label', `Quantity for ${line.product_title || line.title}`);
          for (const [action, symbol] of [['decrease-card', '−'], ['', ''], ['increase-card', '+']]) {
            const control = document.createElement(action ? 'button' : 'span');
            if (action) {
              control.type = 'button'; control.dataset.cartAction = action;
              control.setAttribute('aria-label', `${action === 'decrease-card' ? 'Decrease' : 'Increase'} quantity for ${line.product_title || line.title}`);
            } else control.setAttribute('aria-live', 'polite');
            control.textContent = symbol; quantity.append(control);
          }
          add.after(quantity);
        }
        const count = quantity.querySelector('span[aria-live]');
        if (count.textContent !== String(line.quantity)) count.textContent = String(line.quantity);
        quantity.querySelectorAll('button').forEach(control => {
          control.dataset.cartKey = line.key; control.dataset.cartQuantityValue = String(line.quantity);
        });
      });
    }

    isAccessoryProduct(product) {
      const excluded = (this.dataset.accessoryHandles || '').split(',');
      const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
      return excluded.includes(product.handle) || /accessor/i.test(product.type || product.product_type || '') || tags.some(tag => /^(pbn-accessory|accessories)$/i.test(tag.trim()));
    }

    async loadViewedRecommendations(version) {
      const rail = this.querySelector('[data-cart-bestsellers]');
      if (!rail || rail.dataset.viewedLoading === 'true') return;
      const history = this.readProductVisits();
      // Services have their own parent/consent workflow; a recommendation must
      // never offer them as an ordinary standalone quick add.
      const excludedHandles = new Set((this.dataset.recommendationExcludedHandles || '').split(',').filter(Boolean));
      const cartProductIds = new Set([...this.querySelectorAll('[data-cart-product-id]')]
        .map((element) => String(element.dataset.cartProductId || ''))
        .filter(Boolean));
      const signature = JSON.stringify([history, [...cartProductIds].sort()]);
      if (rail.dataset.viewedReady === 'true' && rail.dataset.viewedSignature === signature) {
        // A server placeholder may hide the enclosing section while its loaded
        // rail is preserved. A valid cache hit must restore that rail's visibility.
        const section = rail.closest('[data-cart-recommendations-section]');
        if (section) section.hidden = rail.children.length === 0;
        return;
      }
      rail.dataset.viewedLoading = 'true';
      try {
        let incomplete = false;
        const results = await Promise.all(history.map(item => this.fetchProduct(item.handle).catch(() => {
          incomplete = true;
          return null;
        })));
        const nodes = [], viewedSeedIds = [], seenIds = new Set(), seenHandles = new Set();
        const accept = (card, productId) => {
          const handle = card.dataset.productHandle;
          const id = String(productId || '');
          if (!handle || !id || seenIds.has(id) || seenHandles.has(handle) || cartProductIds.has(id) ||
              handle === this.dataset.protectionHandle || excludedHandles.has(handle) || nodes.length >= 20) return;
          seenIds.add(id); seenHandles.add(handle); nodes.push(card);
        };
        for (const product of results) {
          if (!product?.id || product.available === false || cartProductIds.has(String(product.id)) ||
              product.handle === this.dataset.protectionHandle || this.isAccessoryProduct(product)) continue;
          viewedSeedIds.push(String(product.id));
          const card = this.createProductCard(product);
          const add = card.querySelector('[data-cart-action]');
          if (add && history.find(item => item.handle === product.handle)?.pageOnly) {
            const link = document.createElement('a');
            link.href = safeUrl(product.url, `${this.dataset.rootUrl || '/'}products/${encodeURIComponent(product.handle)}`);
            link.className = add.className; link.textContent = '+';
            link.setAttribute('aria-label', `View ${product.title}`); add.replaceWith(link);
          }
          accept(card, product.id);
        }
        const seedIds = cartProductIds.size ? [...cartProductIds] : viewedSeedIds;
        if (nodes.length < 20 && this.dataset.recommendationsUrl && seedIds.length) {
          const requests = [...new Set(seedIds)].slice(0, 4).map((productId) => {
            const cacheKey = `${this.dataset.rootUrl}|${this.dataset.currency}|${productId}`;
            const cached = this.recommendationCache.get(cacheKey);
            if (!cached || Date.now() - cached.time >= PRODUCT_CACHE_TTL) {
              const separator = this.dataset.recommendationsUrl.includes('?') ? '&' : '?';
              const request = this.requestJson(`${this.dataset.recommendationsUrl}${separator}product_id=${encodeURIComponent(productId)}&limit=8&intent=related`, {
                headers: { Accept: 'application/json' },
              }).catch((error) => {
                this.recommendationCache.delete(cacheKey);
                throw error;
              });
              this.recommendationCache.set(cacheKey, { time: Date.now(), request });
            }
            return this.recommendationCache.get(cacheKey).request.catch(() => {
              incomplete = true;
              return { products: [] };
            });
          });
          for (const payload of await Promise.all(requests)) {
            for (const product of payload.products || []) {
              if (!product?.id || product.available === false || cartProductIds.has(String(product.id)) ||
                  product.handle === this.dataset.protectionHandle || this.isAccessoryProduct(product)) continue;
              // Recommendations normally include tags; resolve missing marker data before enabling quick add.
              const resolved = product.tags == null ? await this.fetchProduct(product.handle).catch(() => null) : product;
              if (!resolved || resolved.tags == null) { incomplete = true; continue; }
              accept(this.createProductCard(resolved), resolved.id);
              if (nodes.length >= 20) break;
            }
            if (nodes.length >= 20) break;
          }
        }
        if (nodes.length < 20) {
          // Related results overlap and Shopify does not promise a full rail.
          // The existing sales-ranking source fills only the vacant places;
          // it runs outside the serialized merchandise/checkout settlement.
          const ranked = await this.rankedProductCards('all').catch(() => {
            incomplete = true;
            return [];
          });
          for (const card of ranked) {
            accept(card, card.dataset.productId);
            if (nodes.length >= 20) break;
          }
        }
        if (version !== this.renderVersion || !rail.isConnected || !this.isConnected) return;
        const source = rail.cloneNode(false);
        source.append(...nodes);
        this.patchCartNode(rail, source);
        const section = rail.closest('[data-cart-recommendations-section]');
        if (section) section.hidden = nodes.length === 0;
        // Keep available cards after a failed read, but allow the next normal
        // open/render to retry. Successful sparse catalogs still remain cached.
        rail.dataset.viewedReady = incomplete ? 'false' : 'true';
        rail.dataset.viewedSignature = signature;
        this.syncRecommendationQuantities();
      } finally {
        delete rail.dataset.viewedLoading;
        if (version !== this.renderVersion && rail.isConnected && this.isConnected) this.scheduleEnhancements();
      }
    }

    async loadRecommendations(version) {
      return this.loadViewedRecommendations(version);
    }

    createProductCard(product) {
      const variant = (product.variants || []).find((item) => item.available !== false) || product.variants?.[0] || {};
      const card = document.createElement('article');
      const media = document.createElement('div');
      const image = document.createElement('img');
      const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',').map(tag => tag.trim());
      const pageOnly = tags.includes('myart-pn-photo');
      const add = document.createElement(pageOnly ? 'a' : 'button');
      const title = document.createElement('a');
      const price = document.createElement('p');
      const current = document.createElement('strong');
      card.className = 'myart-cart__product-card';
      card.dataset.productHandle = String(product.handle || '');
      media.className = 'myart-cart__product-media';
      media.setAttribute('aria-hidden', 'true');
      image.className = 'myart-cart__product-image';
      setImageSource(image, product.featured_image || product.images?.[0]);
      image.alt = String(product.title || '');
      image.loading = 'lazy';
      image.draggable = false;
      image.width = 146;
      image.height = 146;
      media.appendChild(image);
      add.className = 'myart-cart__product-add';
      if (pageOnly) {
        add.href = safeUrl(product.url, (this.dataset.rootUrl || '/') + 'products/' + encodeURIComponent(product.handle || ''));
      } else {
        add.type = 'button'; add.dataset.cartAction = 'add-product';
      }
      add.dataset.productHandle = String(product.handle || '');
      add.dataset.variantId = String(variant.id || '');
      add.dataset.needsOptions = String((product.variants || []).length > 1);
      add.setAttribute('aria-label', pageOnly ? `Customize ${String(product.title || 'product')}` : `Add ${String(product.title || 'product')} to cart`);
      add.textContent = '+';
      add.disabled = !variant.id || variant.available === false;
      title.className = 'myart-cart__product-title';
      title.href = safeUrl(product.url, `${this.dataset.rootUrl || '/'}products/${encodeURIComponent(product.handle || '')}`);
      title.textContent = String(product.title || '');
      price.className = 'myart-cart__product-price';
      current.textContent = this.formatMoney(variant.price || product.price || 0);
      price.appendChild(current);
      if (Number(variant.compare_at_price || 0) > Number(variant.price || 0)) {
        const compare = document.createElement('s');
        compare.textContent = this.formatMoney(variant.compare_at_price);
        price.appendChild(compare);
      }
      card.append(media, add, title, price);
      return card;
    }

    async renderBundles(version) {
      const section = this.querySelector('[data-cart-bundles-section]');
      const container = this.querySelector('[data-cart-bundles]');
      const bundles = Array.isArray(window.BUNDLES) ? window.BUNDLES : [];
      if (!section || !container || !bundles.length || container.dataset.bundlesReady === 'true') return;

      const failures = new Map();
      const noteFailure = (handle, error) => {
        const safeHandle = /^[a-z0-9][a-z0-9-]{0,254}$/.test(handle) ? handle : 'configured-product';
        const status = Number(error?.status);
        const category = error?.name === 'CartTimeoutError' ? 'timeout' : error?.name === 'AbortError' ? 'cancelled' :
          Number.isInteger(status) && status >= 100 && status <= 599 ? 'http' :
            error?.category === 'invalid-json' ? 'invalid-json' : error?.category === 'invalid-product' ? 'invalid-product' : 'request-failed';
        failures.set(safeHandle, { handle: safeHandle, category, ...(category === 'http' ? { status } : {}) });
        return null;
      };

      const cards = await Promise.all(bundles.slice(0, 8).map(async (bundle) => {
        const bundleHandle = normalizeHandle(bundle.bundle_product_handle);
        if (!bundleHandle) return null;
        const bundleProduct = await this.fetchProduct(bundleHandle).catch((error) => noteFailure(bundleHandle, error));
        if (!bundleProduct) return null;
        if (!Array.isArray(bundleProduct.variants) || !bundleProduct.variants.length) {
          return noteFailure(bundleHandle, { category: 'invalid-product' });
        }
        const componentHandles = (bundle.products || []).map(normalizeHandle).filter(Boolean).slice(0, 6);
        const components = (await Promise.all(componentHandles.map((handle) => this.fetchProduct(handle).catch((error) => noteFailure(handle, error))))).filter(Boolean);
        return this.createBundleCard(bundle, bundleProduct, components);
      }));
      if (version !== this.renderVersion || !container.isConnected) return;
      const validCards = cards.filter(Boolean);
      container.replaceChildren(...validCards);
      if (validCards.length && !failures.size) container.dataset.bundlesReady = 'true';
      section.hidden = validCards.length === 0;
      if (!validCards.length && failures.size && this.lastBundleFailureVersion !== version) {
        this.lastBundleFailureVersion = version;
        // No error bodies, URLs, cart data or customer properties enter logs.
        console.warn('Cart bundles unavailable.', { configuredBundles: bundles.length, failedProducts: [...failures.values()] });
      }
    }

    getBundleComponentPrices(bundle, variant, components) {
      const handles = Array.isArray(bundle.products) ? bundle.products.map(normalizeHandle) : [];
      const rate = Number(bundle.discount);
      const total = Number(variant.price);
      const compare = Number(variant.compare_at_price);
      if (!handles.length || handles.length !== components.length || handles.some((handle, index) =>
        !handle || handle !== components[index]?.handle) || !Number.isFinite(rate) || rate <= 0 || rate >= 100 ||
        !Number.isSafeInteger(total) || total < 0 || !Number.isSafeInteger(compare) || compare <= total) return null;

      const prices = [];
      for (const component of components) {
        const variants = Array.isArray(component.variants) ? component.variants : [];
        const price = Number(variants[0]?.price);
        // Product-list settings do not identify a component variant. A variable
        // price would imply a selection that the bundle has never confirmed.
        if (!variants.length || !Number.isSafeInteger(price) || price < 0 ||
          variants.some((item) => Number(item.price) !== price)) return null;
        prices.push(price);
      }

      // Preserve the source's advertised calculation only when every displayed
      // rounded amount reconciles to the real bundle price. These are display
      // comparisons, not Shopify line allocations; never distribute spare cents.
      const discounted = prices.map((price) => Math.round(Number((price / 100 * (1 - rate / 100)).toFixed(2)) * 100));
      if (prices.reduce((sum, price) => sum + price, 0) !== compare ||
        discounted.reduce((sum, price) => sum + price, 0) !== total) return null;
      return discounted.map((price, index) => ({ price, compare: prices[index] }));
    }

    createBundleCard(bundle, product, components) {
      const variant = (product.variants || []).find((item) => item.available !== false) || product.variants?.[0];
      if (!variant) return null;
      const componentPrices = this.getBundleComponentPrices(bundle, variant, components);
      const comparePrice = Number(variant.compare_at_price || 0);
      const discount = comparePrice > Number(variant.price)
        ? Math.round((comparePrice - Number(variant.price)) / comparePrice * 100) : 0;
      const card = document.createElement('article');
      const header = document.createElement('div');
      const heading = document.createElement('h4');
      const save = document.createElement('span');
      const products = document.createElement('div');
      const footer = document.createElement('div');
      const total = document.createElement('p');
      const totalPrice = document.createElement('strong');
      const add = document.createElement('button');
      card.className = 'myart-cart__bundle-card';
      header.className = 'myart-cart__bundle-header';
      heading.textContent = String(bundle.title || product.title || 'Bundle');
      save.className = 'myart-cart__bundle-save';
      const saveLabel = document.createElement('span');
      const saveValue = document.createElement('strong');
      saveLabel.textContent = 'Save';
      saveValue.textContent = this.formatMoney(Math.max(0, comparePrice - Number(variant.price)));
      save.append(saveLabel, saveValue);
      save.hidden = discount <= 0;
      header.append(heading, save);
      products.className = 'myart-cart__bundle-products';
      products.dataset.count = String(components.length);
      for (const [index, component] of components.entries()) {
        const link = document.createElement('a');
        const image = document.createElement('img');
        const title = document.createElement('span');
        const prices = document.createElement('span');
        const componentPrice = document.createElement('strong');
        link.className = 'myart-cart__bundle-product';
        link.href = safeUrl(component.url, `${this.dataset.rootUrl || '/'}products/${encodeURIComponent(component.handle || '')}`);
        setImageSource(image, component.featured_image || component.images?.[0]);
        image.alt = String(component.title || '');
        image.loading = 'lazy';
        image.width = 120;
        image.height = 120;
        title.textContent = String(component.title || '');
        prices.className = 'myart-cart__bundle-product-price';
        const componentVariant = (component.variants || []).find((item) => item.available !== false) || component.variants?.[0];
        const displayedPrice = componentPrices?.[index]?.price ?? componentVariant?.price ?? component.price;
        const displayedCompare = componentPrices?.[index]?.compare ?? componentVariant?.compare_at_price;
        componentPrice.textContent = this.formatMoney(displayedPrice);
        prices.appendChild(componentPrice);
        if (!componentPrices) {
          prices.title = 'Sold separately';
          prices.setAttribute('aria-label', `Sold separately: ${this.formatMoney(displayedPrice)}`);
        }
        if (Number(displayedCompare || 0) > Number(displayedPrice || 0)) {
          const comparison = document.createElement('s');
          comparison.textContent = this.formatMoney(displayedCompare);
          prices.appendChild(comparison);
        }
        link.append(image, title, prices);
        products.appendChild(link);
      }
      footer.className = 'myart-cart__bundle-footer';
      total.className = 'myart-cart__bundle-total';
      const totalLabel = document.createElement('span');
      const totalPrices = document.createElement('span');
      totalLabel.className = 'myart-cart__bundle-label';
      totalLabel.textContent = discount > 0 ? `Total bundle price (${discount}% OFF)` : 'Total bundle price';
      totalPrices.className = 'myart-cart__bundle-prices';
      totalPrice.textContent = this.formatMoney(variant.price);
      totalPrices.appendChild(totalPrice);
      if (Number(variant.compare_at_price || 0) > Number(variant.price)) {
        const compare = document.createElement('s');
        compare.textContent = this.formatMoney(variant.compare_at_price);
        totalPrices.appendChild(compare);
      }
      total.append(totalLabel, totalPrices);
      add.className = 'myart-cart__bundle-add';
      add.type = 'button';
      add.dataset.cartAction = 'add-bundle';
      add.dataset.productHandle = String(product.handle || '');
      add.dataset.variantId = String(variant.id || '');
      add.dataset.bundleId = String(bundle.id || '');
      add.dataset.needsOptions = String((product.variants || []).length > 1);
      add.textContent = 'Add Bundle To Cart';
      add.disabled = variant.available === false;
      footer.append(total, add);
      card.append(header, products, footer);
      return card;
    }

    formatMoney(cents) {
      const value = Number(cents || 0) / 100;
      const currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency,
        }).format(value);
      } catch {
        return `${currency} ${value.toFixed(2)}`;
      }
    }

    announce(message) {
      const status = this.querySelector('[data-cart-status]');
      if (!status) return;
      status.textContent = '';
      queueMicrotask(() => { status.textContent = String(message || 'Cart updated.'); });
    }

    clearError() {
      const error = this.querySelector('[data-cart-error]');
      if (!error) return;
      error.hidden = true;
      error.textContent = '';
      const recovery = this.querySelector('[data-cart-recovery]');
      if (recovery) recovery.hidden = true;
    }

    showError(message) {
      const error = this.querySelector('[data-cart-error]');
      if (!error) return;
      error.hidden = false;
      error.textContent = String(message || 'The cart could not be updated. Please try again.');
      const recovery = this.querySelector('[data-cart-recovery]');
      if (recovery) recovery.hidden = false;
      // Keep normal updates still; an actionable failure must be visible even
      // when the shopper was working near the bottom of the drawer.
      const scroll = this.querySelector('[data-cart-scroll]');
      if (scroll) scroll.scrollTop = 0;
    }

    handleOperationError(error) {
      if (error?.name === 'AbortError') return;
      console.error('Cart operation failed.', error);
      this.showError(error?.message || 'The cart could not be updated. Please try again.');
    }
  }

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', MyArtCartDrawer);
  }
})();
